import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, Search, ShieldCheck, ShieldOff, Ban, BadgeCheck, Fingerprint,
  Globe, AlertTriangle, Lock, X, Check, ScrollText, Link2, Repeat, Plus, Trash2,
  ChevronUp, ChevronDown, RotateCcw, ShieldAlert, FileCheck, AtSign, Unlock, Gift, MailCheck,
  Star, CreditCard, History, TrendingUp, Ban as BanIcon, Tag, MoreHorizontal, Pencil, Megaphone,
  Image as ImageIcon, ChevronLeft, ChevronRight, SlidersHorizontal, Cake, ArrowRight,
} from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";
import { StatusChip, idChipState, idTaxonomyChip } from "@/components/StatusChip";
import { PayoutChip } from "@/components/admin/PayoutChip";
import { Switch } from "@/components/ui/switch";
import { resolveAccountKind, roleRank, isAdminRole, isSuperAdmin, hasSuperPrivileges } from "@/lib/accountRole";
import { useAuth } from "@/context/AuthContext";
import { FoundingSeatChip } from "@/components/FoundingSeatChip";
import { BetaCompPanel } from "@/components/admin/BetaCompPanel";
import { FoundingCompsExpiring } from "@/components/admin/FoundingCompsExpiring";
import { BetaOverridesNeedingReview } from "@/components/admin/BetaOverridesNeedingReview";
import { ListingFeeOverridesReview } from "@/components/admin/ListingFeeOverridesReview";
import { BetaTesterBulkGrant } from "@/components/admin/BetaTesterBulkGrant";
import { ResendSyncPanel } from "@/components/admin/ResendSyncPanel";

// Mirrors the backend VERIFICATION_EXEMPT_EMAILS — hide the single-account revoke control for
// these (the owner's two accounts + the public demo). The API is the real guard; this is UX.
const REVERIFY_EXEMPT_EMAILS = ["j.connor.sharp@gmail.com", "connor@opticswap.com", "demo@opticswap.com"];

const ROLE_RANK = { user: 0, admin: 2, super_admin: 3 };

const TONE = {
  base: "text-olive-bright border-olive-bright/50",
  restricted: "text-volt border-volt/50",
  disabled: "text-blaze border-blaze/50",
};

function StatusBadge({ ok, yes = "Allowed", no = "Restricted" }) {
  return (
    <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${ok ? "text-olive-bright border-olive-bright/50" : "text-blaze border-blaze/50"}`}>
      {ok ? yes : no}
    </span>
  );
}

function PermissionRow({ uid, item, onChanged }) {
  const [evidence, setEvidence] = useState(item.evidence_ref || "");
  const [busy, setBusy] = useState(false);

  // Single toggle drives everything. ON = permitted (olive), OFF = not permitted. Restricted
  // categories require the evidence ref before they can be switched ON. Every flip is audited
  // server-side exactly as the old buttons were.
  const flip = async (nextOn) => {
    if (!nextOn && !window.confirm(`Revoke posting in "${item.category}"? Their active listings in this category will be unpublished and they'll be notified.`)) return;
    if (nextOn && item.kind === "restricted" && !evidence.trim()) {
      toast.error(`Enter the ${item.evidence_label || "evidence reference"} before enabling ${item.category}.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${uid}/permissions`, {
        category: item.category, allowed: nextOn, evidence_ref: evidence || null,
      });
      toast.success(nextOn ? `Granted ${item.category}` : `Revoked ${item.category}${data.unpublished ? ` · ${data.unpublished} listing(s) unpublished` : ""}`);
      onChanged(data.permission_grid);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Prohibited platform-wide (firearms / NFA): a locked, non-interactive OFF switch.
  if (item.kind === "disabled") {
    return (
      <div className="flex items-center justify-between gap-3 py-2.5 px-3" data-testid={`perm-row-${item.category}`}>
        <div className="min-w-0">
          <div className="font-body text-sm text-foreground flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-blaze" /> {item.category}</div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground">{item.reason || "Prohibited platform-wide"}</div>
        </div>
        <span title="Prohibited platform-wide — not available to any account, not a per-user setting.">
          <Switch checked={false} disabled data-testid={`perm-switch-${item.category}`} aria-label={`${item.category} prohibited platform-wide`} />
        </span>
      </div>
    );
  }

  const needsEvidence = item.kind === "restricted" && !item.allowed;
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 px-3" data-testid={`perm-row-${item.category}`}>
      <div className="min-w-0">
        <div className="font-body text-sm text-foreground flex items-center gap-2">
          {item.category}
          {item.kind === "restricted" && <span className={`font-mono text-[9px] uppercase px-1 border ${TONE.restricted}`}>Restricted</span>}
        </div>
        {item.evidence_required && (
          <div className="text-[10px] font-mono uppercase text-muted-foreground">{item.evidence_label}{item.evidence_ref ? ` · ref: ${item.evidence_ref}` : ""}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {needsEvidence && (
          <input value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Evidence ref"
            data-testid={`perm-evidence-${item.category}`}
            className="w-28 bg-input border border-sniper focus:border-olive outline-none px-2 py-1 text-foreground font-mono text-[11px]" />
        )}
        <Switch
          checked={!!item.allowed}
          disabled={busy}
          onCheckedChange={(v) => flip(v)}
          data-testid={`perm-switch-${item.category}`}
          aria-label={`${item.allowed ? "Permitted" : "Not permitted"}: ${item.category}`}
          className="data-[state=checked]:bg-[#57682A]"
        />
      </div>
    </div>
  );
}

function LinkedAccounts({ userId, username }) {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/accounts/${userId}/links`).then(({ data }) => setData(data)).catch(() => {});
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/accounts?search=${encodeURIComponent(query.trim())}&limit=6`).then(({ data }) => setResults(data.accounts.filter((a) => a.user_id !== userId))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [query, userId]);

  const link = async (target) => {
    try { await api.post(`/admin/accounts/${userId}/links`, { target_user_id: target.user_id }); toast.success(`Linked @${target.username}`); setQuery(""); setResults([]); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const unlink = async (m) => {
    if (!window.confirm(`Unlink @${m.username} from @${username}?`)) return;
    try { await api.delete(`/admin/accounts/${userId}/links/${m.user_id}`); toast.success("Unlinked."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="border border-sniper" data-testid="linked-accounts">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <Link2 className="w-4 h-4 text-olive-bright" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Linked Accounts</h3>
        <span className="font-mono text-[10px] uppercase text-muted-foreground ml-auto">switcher set</span>
      </div>
      <div className="p-3 space-y-2">
        {(data?.members || []).length === 0 && <p className="font-mono text-[11px] uppercase text-muted-foreground">No linked accounts. Add one below to enable switching.</p>}
        {(data?.members || []).map((m) => (
          <div key={m.user_id} className="flex items-center justify-between gap-2 border border-sniper px-2.5 py-1.5" data-testid={`linked-row-${m.username}`}>
            <span className="font-mono text-xs text-foreground">@{m.username} <span className="text-muted-foreground uppercase text-[10px]">{m.role}</span>{m.is_primary && <span className="ml-1 text-[9px] uppercase text-olive-bright">primary</span>}</span>
            <button onClick={() => unlink(m)} data-testid={`unlink-${m.username}`} className="p-1 text-muted-foreground hover:text-blaze"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="relative">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search account to link…" data-testid="link-search"
            className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-xs" />
          {results.length > 0 && (
            <div className="absolute z-10 left-0 right-0 border border-sniper bg-surface divide-y divide-sniper max-h-40 overflow-y-auto">
              {results.map((r) => (
                <button key={r.user_id} onClick={() => link(r)} data-testid={`link-pick-${r.username}`}
                  className="w-full text-left px-3 py-2 font-mono text-xs text-secondary-foreground hover:bg-white/5 hover:text-foreground flex items-center gap-1"><Plus className="w-3 h-3" /> @{r.username} · {r.email}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const fmtCredits = (cents) => (cents ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const fmtJoined = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};
const fmtShortDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }); } catch { return ""; } };
const fmtMoney = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Tier is a DERIVED cache of the membership record — display only; it's never written directly.
const TIER_META = {
  standard: { label: "STANDARD", cls: "text-secondary-foreground border-sniper", rank: 0 },
  preferred: { label: "PREFERRED", cls: "text-olive-bright border-olive-bright/50", rank: 1 },
  elite: { label: "PRO", cls: "text-[#d3a24e] border-[#d3a24e]/60 bg-[#d3a24e]/5", rank: 2 },
};
const STATUS_META = {
  active: { label: "active", cls: "text-olive-bright" },
  trialing: { label: "trial", cls: "text-olive-bright" },
  canceling: { label: "canceling", cls: "text-amber-400" },
  past_due: { label: "past due", cls: "text-blaze" },
  comped: { label: "comped", cls: "text-volt" },
  none: { label: "free", cls: "text-muted-foreground" },
};
const tierRank = (t) => (TIER_META[(t || "standard").toLowerCase()]?.rank ?? 0);

// Compact at-a-glance stack for the roster TIER column: colored tier badge, billing interval +
// color-coded status/renewal, a visually distinct COMPED tag (never revenue), and founding seat.
function TierStack({ u }) {
  const tier = (u.seller_tier || "standard").toLowerCase();
  const meta = TIER_META[tier] || TIER_META.standard;
  const m = u.membership || {};
  const comped = !!m.comped;
  const status = (m.status || "none").toLowerCase();
  const sMeta = STATUS_META[status] || STATUS_META.none;
  // Read the ONE backend resolver (membership_state) so the label and the tier facet never
  // disagree; fall back to the raw doc only for older payloads that predate the field.
  const hasMembership = u.membership_state ? u.membership_state !== "none" : (comped || status !== "none");
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap" data-testid={`account-tier-${u.username}`}>
      {hasMembership ? (
        <>
          <span className={`inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 border ${meta.cls}`}>{meta.label}</span>
          {comped ? (
            <span className="text-volt border border-volt/60 px-1 py-0.5 bg-volt/10 inline-flex items-center gap-0.5 text-[9px] uppercase" data-testid={`account-comped-${u.username}`}><Gift className="w-2.5 h-2.5" /> comped</span>
          ) : (
            <span className={`text-[9px] normal-case ${sMeta.cls}`} data-testid={`account-billing-${u.username}`}>{m.interval === "year" ? "annual" : "monthly"} · {sMeta.label}</span>
          )}
        </>
      ) : (
        <span className="text-[9px] normal-case text-muted-foreground/70">no membership</span>
      )}
      {u.founding_number ? (
        <FoundingSeatChip seat={u.founding_number} accountType={u.account_type} testid={`account-founding-${u.username}`} />
      ) : null}
    </div>
  );
}

function MembershipControls({ detail, userId, onChanged }) {
  const { user: viewer } = useAuth();
  const canComp = hasSuperPrivileges(viewer?.role);
  const m = detail.membership || {};
  const isComped = !!m.comped;
  const [compTier, setCompTier] = useState("elite");
  const [reason, setReason] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [showComp, setShowComp] = useState(false);
  const [showUncomp, setShowUncomp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mdetail, setMdetail] = useState(null);
  const [stripe, setStripe] = useState({ loading: true });

  useEffect(() => {
    api.get(`/admin/accounts/${userId}/membership-detail`).then(({ data }) => setMdetail(data)).catch(() => setMdetail(null));
    // Live Stripe payment method loads AFTER render — a slow/down Stripe never blocks the drawer.
    // Depend ONLY on userId: `detail` gets a new object identity on every drawer refresh, and keying
    // this effect on it re-fired GET /stripe-billing (→ stripe.Customer.retrieve) on every render —
    // a stale/orphaned customer id then produced a burst of 404s. Fetch once per account.
    setStripe({ loading: true });
    api.get(`/admin/accounts/${userId}/stripe-billing`)
      .then(({ data }) => setStripe({ loading: false, ...data }))
      .catch(() => setStripe({ loading: false, available: false }));
  }, [userId]);

  const md = (mdetail && mdetail.membership) || {};
  const bs = mdetail && mdetail.billing_summary;
  const hist = (mdetail && mdetail.history) || [];
  const tier = (md.effective_tier || detail.seller_tier || m.tier || "standard").toLowerCase();
  const tMeta = TIER_META[tier] || TIER_META.standard;
  const status = isComped ? "comped" : (md.status || m.status || "none").toLowerCase();
  const sMeta = STATUS_META[status] || STATUS_META.none;
  // A paying member (real Stripe subscription) is managed through Stripe, never comp/uncomp.
  const isPaidActive = !isComped && ["active", "past_due", "canceling", "trialing"].includes(status);

  const reasonOk = reason.trim().length >= 20;
  const nameOk = confirmName.trim() === detail.username;
  const compReady = canComp && reasonOk && nameOk;

  const renew = (md.current_period_end || m.current_period_end) ? new Date(md.current_period_end || m.current_period_end).toLocaleDateString() : null;

  const doComp = async () => {
    if (!compReady) return;
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/comp`, { tier: compTier, reason: reason.trim() });
      toast.success(`Comped ${compTier} membership`);
      setReason(""); setConfirmName(""); setShowComp(false); setShowUncomp(false); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doUncomp = async () => {
    if (!canComp || !reasonOk) return;
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/uncomp`, { reason: reason.trim() });
      toast.success("Comped membership removed — back to Standard");
      setReason(""); setShowUncomp(false); setShowComp(false); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // TIER dropdown → drives the comp/uncomp flow (single source of truth). Non-standard =
  // create/update a comped seat; Standard = end the comped membership. Reason stays required.
  const onTierSelect = (e) => {
    const v = (e.target.value || "").toLowerCase();
    setReason(""); setConfirmName("");
    if (v === tier) { setShowComp(false); setShowUncomp(false); return; }
    if (v === "standard") {
      if (isComped) { setShowUncomp(true); setShowComp(false); }
      else { setShowComp(false); setShowUncomp(false); toast.info("Already on Standard."); }
    } else {
      setCompTier(v); setShowComp(true); setShowUncomp(false);
    }
  };
  const cancelTierChange = () => { setShowComp(false); setShowUncomp(false); setReason(""); setConfirmName(""); };
  const tierSelectValue = showComp ? compTier : showUncomp ? "standard" : tier;

  const SEL = "bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs uppercase";
  // Commit buttons are FILLED (real affordance); pure state reads as demoted text/pills.
  const COMMIT = "font-mono uppercase text-[10px] font-bold px-4 py-1.5 transition-colors disabled:bg-sniper disabled:text-muted-foreground disabled:cursor-not-allowed";
  const TA = "w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground text-xs";
  const Field = ({ label, children, tid }) => (
    <div><span className="text-muted-foreground uppercase text-[10px]">{label}</span><div className="text-foreground" data-testid={tid}>{children}</div></div>
  );

  const pm = stripe;
  const failed = Number(md.failed_payment_count || 0);

  return (
    <div className="border border-sniper" data-testid="membership-controls">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <Gift className="w-4 h-4 text-volt" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Membership &amp; Billing</h3>
      </div>
      <div className="p-3 space-y-3 font-mono text-xs">
        {/* Effective tier + status — read as STATE, not controls */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="membership-status-line">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${tMeta.cls}`}>{tMeta.label}</span>
          <span className={`text-[10px] uppercase ${sMeta.cls}`}>{sMeta.label}</span>
          {status === "canceling" && renew && <span className="text-[10px] text-amber-400">· ends {renew}</span>}
          <span className="text-[9px] text-muted-foreground/70 normal-case ml-auto">tier is derived from membership</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <Field label="Billing" tid="mb-billing">{isComped ? "complimentary · no charge" : (md.interval || m.interval) ? ((md.interval || m.interval) === "year" ? "annual" : "monthly") : "—"}</Field>
          <Field label={status === "canceling" ? "Ends" : "Renews"} tid="mb-renew">{renew || "—"}</Field>
          <Field label="Payment method" tid="mb-pm">
            {pm.loading ? <span className="text-muted-foreground">checking Stripe…</span>
              : pm.available && pm.has_card ? <span className="inline-flex items-center gap-1"><CreditCard className="w-3 h-3 text-olive-bright" />{(pm.brand || "card").toUpperCase()} ···· {pm.last4}{pm.exp_month ? ` (${pm.exp_month}/${String(pm.exp_year).slice(-2)})` : ""}</span>
              : pm.available && !pm.has_card ? <span className="text-muted-foreground">no card on file</span>
              : <span className="text-muted-foreground" title={pm.reason || ""}>unavailable</span>}
          </Field>
          <Field label="First payment" tid="mb-firstpaid">{md.first_paid_at ? new Date(md.first_paid_at).toLocaleDateString() : <span className="text-muted-foreground">—</span>}</Field>
        </div>

        {/* Comp reason inline */}
        {isComped && (
          <div className="border border-volt/30 bg-volt/5 px-2.5 py-2" data-testid="mb-comp-reason">
            <span className="text-[10px] uppercase text-volt">Comped by {md.comped_by || m.comped_by || "admin"}{md.comped_at ? ` · ${new Date(md.comped_at).toLocaleDateString()}` : ""}</span>
            {(md.comped_reason) && <div className="text-[11px] text-secondary-foreground normal-case mt-0.5">"{md.comped_reason}"</div>}
          </div>
        )}

        {/* Cancellation */}
        {(md.cancel_reason && (status === "canceling" || status === "none")) && (
          <div className="border border-amber-400/30 bg-amber-400/5 px-2.5 py-2" data-testid="mb-cancel">
            <span className="text-[10px] uppercase text-amber-400">Cancellation{md.canceled_at ? ` · ${new Date(md.canceled_at).toLocaleDateString()}` : ""}</span>
            <div className="text-[11px] text-secondary-foreground normal-case mt-0.5">Reason: {md.cancel_reason}{md.cancel_comment ? ` — "${md.cancel_comment}"` : ""}</div>
          </div>
        )}

        {/* Past due dunning */}
        {status === "past_due" && (
          <div className="border border-blaze/40 bg-blaze/5 px-2.5 py-2" data-testid="mb-pastdue">
            <span className="text-[10px] uppercase text-blaze inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Past due — retry {failed || 1} of 4</span>
            {md.next_retry_at && <div className="text-[11px] text-secondary-foreground normal-case mt-0.5">Next retry {new Date(md.next_retry_at).toLocaleString()}</div>}
          </div>
        )}

        {/* Lifetime revenue — OUR records */}
        {bs && (
          <div className="border border-sniper px-2.5 py-2" data-testid="mb-revenue">
            <div className="flex items-center gap-1.5 text-[10px] uppercase text-olive-bright"><TrendingUp className="w-3 h-3" /> Lifetime revenue <span className="text-muted-foreground normal-case">(our records · since {bs.since})</span></div>
            <div className="mt-1 grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-muted-foreground normal-case">Membership</span><div className="text-foreground">{fmtMoney(bs.membership_revenue_cents)}</div></div>
              <div><span className="text-muted-foreground normal-case">Sales fees</span><div className="text-foreground">{fmtMoney(bs.sales_commission_cents)}</div></div>
              <div><span className="text-muted-foreground normal-case">Total</span><div className="text-olive-bright font-bold">{fmtMoney(bs.total_cents)}</div></div>
            </div>
            <div className="text-[9px] text-muted-foreground/70 normal-case mt-1">{bs.payment_count} membership payment{bs.payment_count === 1 ? "" : "s"} recorded</div>
          </div>
        )}

        {/* Tier / lifecycle history from Audit Events */}
        <details className="border border-sniper" data-testid="mb-history">
          <summary className="px-2.5 py-1.5 cursor-pointer text-[10px] uppercase text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"><History className="w-3 h-3" /> Tier &amp; billing history ({hist.length})</summary>
          <div className="px-2.5 pb-2 max-h-56 overflow-y-auto space-y-1.5">
            {hist.length === 0 ? <p className="text-[10px] text-muted-foreground normal-case">No recorded events yet.</p> : hist.map((h, i) => (
              <div key={i} className="border-l-2 border-sniper pl-2 py-0.5" data-testid={`mb-history-row-${i}`}>
                <div className="text-[10px] text-foreground uppercase">{(h.action || "").replace("membership.", "").replace("accounts.", "").replace("_", " ")}</div>
                <div className="text-[10px] text-secondary-foreground normal-case">{h.summary}</div>
                <div className="text-[9px] text-muted-foreground normal-case">{h.at ? new Date(h.at).toLocaleString() : ""}{h.actor ? ` · ${h.actor}` : ""}</div>
              </div>
            ))}
            {mdetail && mdetail.history_note && <p className="text-[9px] text-muted-foreground/70 normal-case pt-1 border-t border-sniper">{mdetail.history_note}</p>}
          </div>
        </details>

        {/* ---- Actions: TIER dropdown drives comp/uncomp (single source of truth) ---- */}
        {!canComp ? (
          <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1" data-testid="membership-locked-note"><Lock className="w-3 h-3" /> Changing a member's tier requires super admin.</p>
        ) : isPaidActive ? (
          <div className="space-y-1" data-testid="membership-paid-note">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase text-muted-foreground w-12">Tier</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${tMeta.cls}`}>{tMeta.label}</span>
              <span className="text-volt text-[9px] uppercase">paid</span>
            </div>
            <p className="text-[10px] text-muted-foreground normal-case inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Paying member — the plan is managed through Stripe (Change Plan / Manage Billing), not comp.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="tier-control">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase text-muted-foreground w-12">Tier</span>
              <select value={tierSelectValue} onChange={onTierSelect} data-testid="tier-select" className={SEL}>
                <option value="standard">Standard</option>
                <option value="elite">Pro</option>
              </select>
              <span className="text-[9px] text-muted-foreground/70 normal-case">Pro = complimentary comped seat</span>
            </div>

            {showComp && (
              <div className="border border-volt/40 bg-volt/5 p-3 space-y-3" data-testid="comp-panel">
                <p className="text-[10px] uppercase text-volt flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> {isComped ? "Changing" : "Granting"} a comped <span className="font-bold">{compTier}</span> seat — real money given away, logged to Audit Events.</p>
                <div>
                  <label className="block text-[10px] uppercase text-secondary-foreground mb-1">Reason <span className="text-muted-foreground normal-case">(Audit Events · min 20 chars)</span></label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="comp-reason" rows={2}
                    placeholder="e.g. Founding ambassador — 12-month complimentary Elite seat per partnership" className={TA} />
                  {!reasonOk && <p className="text-[10px] text-blaze" data-testid="comp-reason-error">Reason must be at least 20 characters ({reason.trim().length}/20).</p>}
                </div>
                <div>
                  <label className="block text-[10px] uppercase text-secondary-foreground mb-1">Type the username <span className="text-foreground lowercase">{detail.username}</span> to confirm</label>
                  <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} data-testid="comp-confirm-username" placeholder={detail.username}
                    className="w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
                  {!nameOk && <p className="text-[10px] text-blaze" data-testid="comp-confirm-error">Username does not match — type <span className="text-foreground">{detail.username}</span> exactly.</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={doComp} disabled={!compReady || busy} data-testid="comp-btn" className={`${COMMIT} bg-volt text-black hover:bg-volt/80`}>Grant comped {compTier}</button>
                  <button onClick={cancelTierChange} data-testid="comp-cancel-btn" className="font-mono uppercase text-[10px] px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {showUncomp && (
              <div className="border border-blaze/30 bg-blaze/5 p-3 space-y-2" data-testid="uncomp-panel">
                <p className="text-[10px] uppercase text-blaze flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Ending the comped seat — the member drops to Standard.</p>
                <label className="block text-[10px] uppercase text-secondary-foreground">Reason to remove <span className="text-muted-foreground normal-case">(Audit Events · min 20 chars)</span></label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="uncomp-reason" rows={2} className={TA} />
                {!reasonOk && <p className="text-[10px] text-blaze" data-testid="uncomp-reason-error">Reason must be at least 20 characters ({reason.trim().length}/20).</p>}
                <div className="flex gap-2">
                  <button onClick={doUncomp} disabled={!reasonOk || busy} data-testid="uncomp-btn" className={`${COMMIT} bg-blaze text-white hover:bg-blaze/80`}>Remove comp</button>
                  <button onClick={cancelTierChange} data-testid="uncomp-cancel-btn" className="font-mono uppercase text-[10px] px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountTypeControls({ detail, userId, onChanged }) {
  const cur = detail.account_type || "unclassified";
  const [draft, setDraft] = useState(cur === "unclassified" ? "" : cur);
  const [bizName, setBizName] = useState(detail.business_name || detail.name || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const REASON_MIN = 5;
  const changed = !!draft && draft !== cur;
  const needsName = draft === "business";
  const ready = changed && reason.trim().length >= REASON_MIN && (!needsName || bizName.trim().length >= 2) && !busy;
  const COMMIT = "font-mono uppercase text-[10px] font-bold px-4 py-1.5 transition-colors disabled:bg-sniper disabled:text-muted-foreground disabled:cursor-not-allowed";
  const TONE = {
    individual: "text-olive-bright border-olive/50",
    business: "text-teal-300 border-teal-300/50",
    unclassified: "text-amber-400 border-amber-400/60",
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/account-type`, {
        account_type: draft,
        business_name: needsName ? bizName.trim() : undefined,
        reason: reason.trim(),
      });
      toast.success(`Account type set to ${draft}.`);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not update account type.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-sniper p-3 space-y-3" data-testid="account-type-controls">
      <div className="flex items-center gap-2">
        <h3 className="font-heading uppercase tracking-tight text-foreground text-sm">Account Type</h3>
        <span className={`text-[10px] uppercase px-1.5 py-0.5 border ${TONE[cur] || TONE.unclassified}`} data-testid="drawer-account-type">
          {cur}{detail.business_verification_status ? ` · ${detail.business_verification_status}` : ""}
        </span>
      </div>
      <p className="font-mono text-[10px] text-muted-foreground leading-relaxed">
        Members can't set this — it drives the founding-seat pools. Setting Business here counts as verifying the business.
      </p>
      <select value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="account-type-select"
        className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs uppercase">
        <option value="">— select —</option>
        <option value="individual">Individual</option>
        <option value="business">Business</option>
      </select>
      {needsName && (
        <div>
          <label className="font-mono text-[10px] uppercase text-muted-foreground">Legal business name</label>
          <input value={bizName} onChange={(e) => setBizName(e.target.value)} data-testid="account-type-business-name"
            className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
        </div>
      )}
      {changed && (
        <div className="space-y-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="account-type-reason"
            placeholder={`Reason (min ${REASON_MIN} chars)`}
            className="w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
          <button onClick={save} disabled={!ready} data-testid="account-type-save" className={`${COMMIT} bg-olive text-white hover:bg-olive-bright`}>
            {busy ? "Saving…" : "Save account type"}
          </button>
        </div>
      )}
    </div>
  );
}

// Ambassador status is DERIVED from the /ambassadors roster (single source of truth). This panel
// is READ-ONLY: there is no "Make Ambassador" control here any more. Linking a PUBLISHED roster
// entry to an account (on the Ambassadors page) grants a comped Pro membership and cancels any
// paid Stripe subscription at period end; unlinking reverses it.
function AmbassadorControls({ detail, userId, onChanged }) {
  if (isAdminRole(detail.role)) return null;
  const roster = detail.ambassador_roster || {};
  const isAmb = !!roster.is_ambassador;
  const entries = roster.linked_entries || [];
  const published = entries.filter((e) => e.status === "published");
  const StatusChipEl = () => {
    if (roster.has_published_link) {
      return (
        <span className="text-[10px] uppercase px-1.5 py-0.5 border" style={{ color: "#E08A4F", borderColor: "#C2571F80" }} data-testid="drawer-ambassador-status">
          Ambassador{roster.comped ? " · comped Pro" : ""}
        </span>
      );
    }
    if (isAmb) {
      return (
        <span className="text-[10px] uppercase px-1.5 py-0.5 border border-sniper text-muted-foreground" data-testid="drawer-ambassador-status">
          Ambassador (legacy)
        </span>
      );
    }
    return (
      <span className="text-[10px] uppercase px-1.5 py-0.5 border border-sniper text-muted-foreground" data-testid="drawer-ambassador-status">
        Not an ambassador
      </span>
    );
  };

  return (
    <div className="border border-[#C2571F]/50 p-3 space-y-2" data-testid="ambassador-controls">
      <div className="flex items-center gap-2 flex-wrap">
        <Megaphone className="w-4 h-4" style={{ color: "#C2571F" }} />
        <h3 className="font-heading uppercase tracking-tight text-foreground text-sm">Ambassador</h3>
        <StatusChipEl />
      </div>

      {published.length > 0 ? (
        <p className="font-mono text-[10px] text-muted-foreground leading-relaxed" data-testid="ambassador-roster-linked">
          Linked to published roster {published.length > 1 ? "entries" : "entry"}:{" "}
          <span className="text-foreground">{published.map((e) => e.display_name || e.ambassador_id).join(", ")}</span>.
          {roster.comped ? " Their Pro membership is comped." : ""}
        </p>
      ) : entries.length > 0 ? (
        <p className="font-mono text-[10px] text-muted-foreground leading-relaxed" data-testid="ambassador-roster-draft">
          Linked to a DRAFT roster entry ({entries.map((e) => e.display_name || e.ambassador_id).join(", ")}) — not an Ambassador until it's published.
        </p>
      ) : isAmb ? (
        <p className="font-mono text-[10px] text-muted-foreground leading-relaxed" data-testid="ambassador-roster-legacy">
          Legacy Ambassador (set before the roster unification). Link a published roster entry to bring it under the roster.
        </p>
      ) : (
        <p className="font-mono text-[10px] text-muted-foreground leading-relaxed" data-testid="ambassador-roster-none">
          Not an Ambassador. Status is granted by linking a PUBLISHED roster entry on the Ambassadors page.
        </p>
      )}

      <a href="/admin/ambassadors" data-testid="ambassador-roster-link"
        className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] font-bold px-3 py-1.5 border border-[#C2571F]/60 transition-colors hover:bg-[#C2571F]/10"
        style={{ color: "#E08A4F" }}>
        Manage on Ambassadors roster
      </a>
    </div>
  );
}


// Beta Tester recognition + fee override. Granting the badge auto-starts a 0% platform-fee
// override (shipping-only) for 6 months; when the review date passes the override keeps applying
// but is FLAGGED for an admin decision (adjust rate / extend the clock / clear it) — never
// silently reverted. Both the badge and the override are managed here.
function BetaTesterControls({ detail, userId, onChanged }) {
  const fo = detail.fee_override && detail.fee_override.active ? detail.fee_override : null;
  const earned = !!detail.beta_tester_earned;
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState(fo ? String(fo.rate ?? 0) : "0");
  const [months, setMonths] = useState("6");

  useEffect(() => { setRate(fo ? String(fo.rate ?? 0) : "0"); }, [fo && fo.rate]);

  if (isAdminRole(detail.role)) return null;

  const reviewNeeded = !!(fo && fo.review_needed);
  const expires = fo && fo.expires_at ? new Date(fo.expires_at) : null;
  const expiresLabel = expires && !isNaN(expires.getTime())
    ? expires.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  const expired = expires && !isNaN(expires.getTime()) ? expires.getTime() <= Date.now() : false;
  const BTN = "font-mono uppercase text-[10px] px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  const setBadge = async (granted) => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/recognition`, { badge: "beta_tester", granted });
      toast.success(granted ? "Beta Tester granted — 0% fee, 6-month clock started" : "Beta Tester badge revoked");
      onChanged && onChanged();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const saveOverride = async (resetClock) => {
    const r = Math.max(0, Math.min(100, parseFloat(rate) || 0));
    setBusy(true);
    try {
      const body = { active: true, rate: r, source: "beta_tester", clear_review: true };
      if (resetClock) body.months = Math.max(1, Math.min(60, parseInt(months, 10) || 6));
      await api.post(`/admin/accounts/${userId}/fee-override`, body);
      toast.success(resetClock ? `Override ${r}% · review reset to ${body.months} mo` : `Fee override set to ${r}%`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/fee-override`, { active: false });
      toast.success("Fee override cleared");
      onChanged && onChanged();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-volt/50 p-3 space-y-3" data-testid="beta-tester-controls">
      <div className="flex items-center gap-2 flex-wrap">
        <Star className="w-4 h-4 text-volt" />
        <h3 className="font-heading uppercase tracking-tight text-foreground text-sm">Beta Tester</h3>
        {earned ? (
          <span className="text-[10px] uppercase px-1.5 py-0.5 border text-volt border-volt/50" data-testid="beta-tester-status">Beta Tester · ON</span>
        ) : (
          <span className="text-[10px] uppercase px-1.5 py-0.5 border border-sniper text-muted-foreground" data-testid="beta-tester-status">Not a beta tester</span>
        )}
        {fo && (
          <span className="text-[10px] uppercase px-1.5 py-0.5 border text-olive-bright border-olive-bright/50 font-mono" data-testid="beta-tester-fee-chip">
            Fee {Number(fo.rate ?? 0)}%
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {earned ? (
          <button disabled={busy} onClick={() => setBadge(false)} data-testid="beta-tester-revoke"
            className={`${BTN} text-blaze border-blaze/50 hover:bg-blaze/10`}>Revoke badge</button>
        ) : (
          <button disabled={busy} onClick={() => setBadge(true)} data-testid="beta-tester-grant"
            className={`${BTN} text-volt border-volt/50 hover:bg-volt/10`}>Grant Beta Tester · 0% · 6 mo</button>
        )}
        <span className="font-mono text-[9px] uppercase text-muted-foreground/70">Grant starts a 0% shipping-only fee for 6 months</span>
      </div>

      {fo ? (
        <div className="border border-sniper p-2.5 space-y-2.5" data-testid="beta-tester-override">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px]">
            <div className="text-muted-foreground uppercase">Current rate</div>
            <div className="text-foreground text-right" data-testid="beta-override-current-rate">{Number(fo.rate ?? 0)}%</div>
            <div className="text-muted-foreground uppercase">Source</div>
            <div className="text-foreground text-right">{fo.source || "custom"}</div>
            <div className="text-muted-foreground uppercase">Review date</div>
            <div className={`text-right ${expired ? "text-volt" : "text-foreground"}`} data-testid="beta-override-review-date">{expiresLabel || "—"}</div>
          </div>

          {reviewNeeded && (
            <p className="font-mono text-[10px] text-volt flex items-start gap-1.5" data-testid="beta-override-review-flag">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Past its review date — still applying at {Number(fo.rate ?? 0)}%. Adjust the rate or extend below, or clear it.
            </p>
          )}

          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Fee rate %</label>
              <input type="number" min={0} max={100} step="0.5" value={rate} onChange={(e) => setRate(e.target.value)}
                data-testid="beta-override-rate-input"
                className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs w-20" />
            </div>
            <button disabled={busy} onClick={() => saveOverride(false)} data-testid="beta-override-save"
              className={`${BTN} text-olive-bright border-olive-bright/50 hover:bg-olive-bright/10`}>Save rate</button>
            <div>
              <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Extend (months)</label>
              <input type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)}
                data-testid="beta-override-months-input"
                className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs w-20" />
            </div>
            <button disabled={busy} onClick={() => saveOverride(true)} data-testid="beta-override-extend"
              className={`${BTN} text-foreground border-sniper hover:border-white/40`}>Save + reset clock</button>
            <button disabled={busy} onClick={clearOverride} data-testid="beta-override-clear"
              className={`${BTN} text-blaze border-blaze/50 hover:bg-blaze/10 ml-auto`}>Clear override</button>
          </div>
        </div>
      ) : (
        <p className="font-mono text-[10px] text-muted-foreground" data-testid="beta-tester-no-override">
          No active fee override. Granting the Beta Tester badge starts one automatically (0% · 6 months).
        </p>
      )}
    </div>
  );
}


function AccountControls({ detail, userId, onChanged }) {
  const { user: viewer } = useAuth();
  const canRoles = isSuperAdmin(viewer?.role);       // role management is the ONE super-only power
  const canSuper = hasSuperPrivileges(viewer?.role); // every other elevated action: admin == super
  const targetElevated = isAdminRole(detail.role);   // target is admin or super_admin
  const curRole = detail.role === "super_admin" ? "super_admin" : detail.role === "admin" ? "admin" : "user";
  const isReset = detail.account_status === "reset";
  const [roleDraft, setRoleDraft] = useState(curRole);
  const [roleReason, setRoleReason] = useState("");
  const [roleConfirm, setRoleConfirm] = useState("");
  const curAccess = (detail.access_status || "active").toLowerCase();
  const [accessDraft, setAccessDraft] = useState(curAccess);
  const [accessReason, setAccessReason] = useState("");
  const [danger, setDanger] = useState(null); // 'delete' | 'reset' | null
  const [dangerReason, setDangerReason] = useState("");
  const [dangerConfirm, setDangerConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [uname, setUname] = useState("");
  const [ureason, setUreason] = useState("");
  const [bmonth, setBmonth] = useState(detail.birth_month ? String(detail.birth_month) : "");
  const [bday, setBday] = useState(detail.birth_day ? String(detail.birth_day) : "");
  const [breason, setBreason] = useState("");
  const [modBusy, setModBusy] = useState(false);

  const REASON_MIN = 10;
  const roleChanged = roleDraft !== curRole;
  const promoting = (ROLE_RANK[roleDraft] ?? 0) > (ROLE_RANK[curRole] ?? 0);
  const roleReasonOk = roleReason.trim().length >= REASON_MIN;
  const roleNameOk = roleConfirm.trim() === detail.username;
  const roleReady = canRoles && roleChanged && roleReasonOk && roleNameOk;
  // Item 3 — gate impossible role demotions in the UI (backend /role still enforces).
  const isSelf = !!viewer && detail.user_id === viewer.user_id;
  const isPermanentOwner = !!detail.is_permanent;
  const isLastSuper = curRole === "super_admin" && typeof detail.super_admin_count === "number" && detail.super_admin_count <= 1;
  const isLastAdmin = (curRole === "admin" || curRole === "super_admin") && typeof detail.admin_count === "number" && detail.admin_count <= 1;
  const demoteBlockReason = isPermanentOwner
    ? "Protected owner account — it can't be demoted from this panel."
    : isLastSuper
    ? "Last super admin — promote another super admin before this one can be demoted."
    : isLastAdmin
    ? "Last admin — promote another admin before this one can be demoted."
    : null;
  const roleOptionBlocked = (optRole) => {
    if (!canRoles) return optRole !== "user";
    const isDemotion = (ROLE_RANK[optRole] ?? 0) < (ROLE_RANK[curRole] ?? 0);
    return isDemotion && !!demoteBlockReason;
  };
  const accessChanged = accessDraft !== curAccess;
  const involvesArchive = accessDraft === "archived" || curAccess === "archived";
  const accessReasonOk = accessReason.trim().length >= 20;
  // Disable / on-hold / restore / archive / un-archive: any admin (admin == super privileges).
  const accessReady = !targetElevated && accessChanged && accessReasonOk && (involvesArchive ? canSuper : true);
  const dangerReasonOk = dangerReason.trim().length >= REASON_MIN;
  const dangerNameOk = dangerConfirm.trim() === detail.username;
  const dangerReady = dangerReasonOk && dangerNameOk;

  const saveRole = async () => {
    if (!roleReady) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${userId}/role`, { role: roleDraft, reason: roleReason.trim() });
      toast.success(promoting ? `Promoted to ${roleDraft.replace("_", " ")}` : `Changed to ${roleDraft.replace("_", " ")}`);
      setRoleReason(""); setRoleConfirm(""); onChanged();
      if (data.admin_count != null) toast.message(`${data.admin_count} admin(s), ${data.super_admin_count} super`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleForumMod = async () => {
    setModBusy(true);
    try {
      const { data } = await api.post(`/admin/forum/moderators/${userId}`, { enabled: !detail.is_forum_moderator });
      toast.success(data.is_forum_moderator ? `@${detail.username} is now a forum moderator` : "Forum moderator removed");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setModBusy(false); }
  };
  const saveStatus = async () => {
    if (!accessReady) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${userId}/status`, { status: accessDraft, reason: accessReason.trim() });
      toast.success(`Account status set to ${accessDraft.replace("_", " ")}`);
      if (data?.billing === "failed") toast.error(`Billing teardown failed — it will retry automatically. ${data.billing_error || ""}`);
      setAccessReason(""); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runDanger = async () => {
    if (!dangerReady) return;
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/${danger}`, { reason: dangerReason.trim() });
      toast.success(danger === "delete" ? "Account deleted (email freed)" : "Account content reset");
      setDanger(null); setDangerReason(""); setDangerConfirm(""); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/account-reset/restore/${userId}`, {});
      toast.success("Account restored"); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const overrideUsername = async () => {
    if (!uname.trim() || ureason.trim().length < 3) { toast.error("Enter a username and a reason (3+ chars)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${userId}/username`, { username: uname.trim(), reason: ureason.trim() });
      toast.success(`Username changed to @${data.username}`);
      setUname(""); setUreason(""); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveBirthday = async () => {
    const both = (!!bmonth && !!bday) || (!bmonth && !bday);
    if (!both) { toast.error("Set both month and day, or clear both."); return; }
    if (breason.trim().length < 5) { toast.error("Enter a reason (5+ chars)."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/birthday`, {
        birth_month: bmonth ? Number(bmonth) : null,
        birth_day: bday ? Number(bday) : null,
        reason: breason.trim(),
      });
      toast.success("Birthday updated.");
      setBreason(""); onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const SEL = "bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs uppercase";
  const BTN = "font-mono uppercase text-[10px] px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  const COMMIT = "font-mono uppercase text-[10px] font-bold px-4 py-1.5 transition-colors disabled:bg-sniper disabled:text-muted-foreground disabled:cursor-not-allowed";
  const curTier = (detail.seller_tier || "standard").toLowerCase();
  const cTierMeta = TIER_META[curTier] || TIER_META.standard;
  const curMem = detail.membership || {};

  return (
    <div className="border border-sniper" data-testid="account-controls">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-olive-bright" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Role, Tier &amp; Lifecycle</h3>
      </div>
      <div className="p-3 space-y-4">
        {/* ROLE */}
        {isSelf ? (
          <div data-testid="role-self-readonly">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase text-muted-foreground w-16">Role</span>
              <span className="font-mono text-[10px] font-bold uppercase px-2 py-0.5 border border-sniper text-foreground" data-testid="role-current-badge">{curRole.replace("_", " ")}</span>
            </div>
            <p className="mt-1 font-mono text-[10px] text-muted-foreground inline-flex items-center gap-1" data-testid="role-self-note">
              <Lock className="w-3 h-3 shrink-0" /> This is your own account — you can't change your own role.
            </p>
          </div>
        ) : (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase text-muted-foreground w-16">Role</span>
            <select value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)} disabled={!canRoles}
              data-testid="role-select" title={canRoles ? undefined : "Requires super admin"}
              className={`${SEL} ${canRoles ? "" : "opacity-60 cursor-not-allowed"}`}>
              <option value="user" disabled={roleOptionBlocked("user")}>User{canRoles && roleOptionBlocked("user") ? " — blocked" : ""}</option>
              <option value="admin" disabled={roleOptionBlocked("admin")} title={canRoles ? undefined : "Requires super admin"}>Admin{!canRoles ? " — requires super admin" : roleOptionBlocked("admin") ? " — blocked" : ""}</option>
              <option value="super_admin" disabled={roleOptionBlocked("super_admin")} title={canRoles ? undefined : "Requires super admin"}>Super Admin{!canRoles ? " — requires super admin" : ""}</option>
            </select>
            {canRoles && !roleChanged && <span className="font-mono text-[10px] text-muted-foreground uppercase">current</span>}
          </div>
          {!canRoles ? (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground inline-flex items-center gap-1" data-testid="role-locked-note" title="Requires super admin">
              <Lock className="w-3 h-3" /> Requires super admin to change roles
            </p>
          ) : demoteBlockReason ? (
            <p className="mt-1 font-mono text-[10px] text-amber-400 inline-flex items-start gap-1" data-testid="role-demote-blocked-note">
              <ShieldAlert className="w-3 h-3 shrink-0 mt-px" /> {demoteBlockReason}
            </p>
          ) : null}
          {canRoles && roleChanged && (
            <div className="mt-2 border border-volt/40 bg-volt/5 p-3 space-y-3" data-testid="role-confirm-panel">
              <p className="font-mono text-[10px] uppercase text-volt flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" />
                {promoting
                  ? `Promoting to ${roleDraft === "super_admin" ? "SUPER ADMIN" : "ADMIN"} — elevated platform control.`
                  : `Demoting to ${roleDraft.replace("_", " ").toUpperCase()} — elevated access revoked.`}
              </p>
              <div>
                <label className="block font-mono text-[10px] uppercase text-secondary-foreground mb-1">Reason for change <span className="text-muted-foreground normal-case">(recorded in Audit Events)</span></label>
                <input value={roleReason} onChange={(e) => setRoleReason(e.target.value)} data-testid="role-reason"
                  placeholder={promoting ? "e.g. Promoting co-founder to platform admin" : "e.g. Stepping down this admin per request"}
                  className="w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
                {!roleReasonOk && <p className="mt-1 font-mono text-[10px] text-blaze" data-testid="role-reason-error">Reason must be at least {REASON_MIN} characters ({roleReason.trim().length}/{REASON_MIN}).</p>}
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase text-secondary-foreground mb-1">Type the username <span className="text-foreground lowercase">{detail.username}</span> to confirm</label>
                <input value={roleConfirm} onChange={(e) => setRoleConfirm(e.target.value)} data-testid="role-confirm-username" placeholder={detail.username}
                  className="w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
                {!roleNameOk && <p className="mt-1 font-mono text-[10px] text-blaze" data-testid="role-confirm-error">Username does not match — type <span className="text-foreground">{detail.username}</span> exactly.</p>}
              </div>
              <button onClick={saveRole} disabled={!roleReady || busy} data-testid="role-save-btn"
                className={`${COMMIT} bg-volt text-black hover:bg-volt/80`}>Save role change</button>
            </div>
          )}
        </div>
        )}
        {/* TIER — READ-ONLY. Derived from the membership record (single source of truth).
            An admin never writes it directly; granting a tier = Comp a tier (Membership panel). */}
        <div data-testid="tier-readonly">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase text-muted-foreground w-16">Tier</span>
            <span className={`font-mono text-[10px] font-bold uppercase px-2 py-0.5 border ${cTierMeta.cls}`} data-testid="tier-current-badge">{cTierMeta.label}</span>
            {curMem.comped ? (
              <span className="font-mono text-[10px] uppercase text-volt border border-volt/50 px-1.5 py-0.5 inline-flex items-center gap-1"><Gift className="w-3 h-3" /> comped</span>
            ) : curMem.status && curMem.status !== "none" ? (
              <span className="font-mono text-[10px] uppercase text-secondary-foreground">{curMem.interval === "year" ? "annual" : "monthly"} · {curMem.status}</span>
            ) : (
              <span className="font-mono text-[10px] uppercase text-muted-foreground">no membership</span>
            )}
          </div>
          <p className="mt-1 font-mono text-[10px] text-muted-foreground inline-flex items-center gap-1" data-testid="tier-derived-note">
            <Lock className="w-3 h-3" /> Tier is derived from the membership. To grant one, use <span className="text-olive-bright">Comp a tier</span> in the Membership panel below.
          </p>
        </div>
        {/* FORUM MODERATOR — forum-scoped role toggle (any admin; grants ZERO admin-dashboard access). */}
        <div data-testid="forum-mod-toggle-row">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase text-muted-foreground w-16">Forum mod</span>
            {targetElevated ? (
              <span className="font-mono text-[10px] uppercase text-secondary-foreground inline-flex items-center gap-1" data-testid="forum-mod-admin-note">
                <ShieldCheck className="w-3 h-3" /> Admins already moderate the forum
              </span>
            ) : (
              <button onClick={toggleForumMod} disabled={modBusy} data-testid="forum-mod-toggle"
                className={`font-mono text-[10px] font-bold uppercase px-2.5 py-1 border transition-colors ${detail.is_forum_moderator ? "bg-olive text-white border-olive" : "border-sniper text-secondary-foreground hover:border-olive/60"}`}>
                {detail.is_forum_moderator ? "Moderator · ON" : "Make moderator"}
              </button>
            )}
          </div>
          {!targetElevated && (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground inline-flex items-start gap-1" data-testid="forum-mod-note">
              <Lock className="w-3 h-3 shrink-0 mt-px" /> Forum-only: pin/lock/move/delete threads, mute/ban members, resolve reports. No admin dashboard access.
            </p>
          )}
        </div>
        {/* ACCESS STATUS — Active / On hold / Disabled / Archived. Disable is reversible (any admin);
            Archive is terminal (super admin only). Admin rows inert. Account DELETION is gone entirely. */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase text-muted-foreground w-16">Status</span>
            <select value={accessDraft} onChange={(e) => setAccessDraft(e.target.value)} disabled={targetElevated}
              data-testid="status-select" className={SEL}>
              <option value="active">Active</option>
              <option value="on_hold">On hold</option>
              <option value="disabled">Disabled</option>
              <option value="archived" disabled={!canSuper} title={canSuper ? undefined : "Requires admin"}>
                Archived{canSuper ? "" : " — admin"}
              </option>
            </select>
            <StatusChip state={curAccess} testId="status-current" />
          </div>
          {targetElevated ? (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground" data-testid="status-admin-note">Admin accounts can't be put on hold, disabled, or archived.</p>
          ) : involvesArchive && !canSuper ? (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground inline-flex items-center gap-1" data-testid="status-locked-note"><Lock className="w-3 h-3" /> Archiving or un-archiving requires admin.</p>
          ) : accessChanged ? (
            <div className="mt-2 border border-amber-400/40 bg-amber-400/5 p-3 space-y-3" data-testid="status-confirm-panel">
              <p className="font-mono text-[10px] uppercase text-amber-400" data-testid="status-effect-note">
                {accessDraft === "disabled" ? "DISABLE (reversible) — login blocked, all sessions revoked, listings hidden. In-flight marketplace orders still resolve; the username is never released."
                  : accessDraft === "archived" ? "ARCHIVE (terminal) — everything Disable does, plus removed from admin lists & analytics, the founding seat is retired, and billing stops (membership subscription canceled now, no refund; saved cards removed). Data is retained; the username is never released."
                  : accessDraft === "on_hold" ? "ON HOLD — can log in & browse, but blocked from listing / buying / messaging / payouts; listings hidden from the feed."
                  : "ACTIVE — restores full access."}
              </p>
              <div>
                <label className="block font-mono text-[10px] uppercase text-secondary-foreground mb-1">Reason <span className="text-muted-foreground normal-case">(min 20 chars — recorded in Audit Events)</span></label>
                <input value={accessReason} onChange={(e) => setAccessReason(e.target.value)} data-testid="status-reason" placeholder="e.g. Multiple scam reports confirmed after review — suspending pending appeal"
                  className="w-full bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs" />
                {!accessReasonOk && <p className="mt-1 font-mono text-[10px] text-blaze" data-testid="status-reason-error">Reason must be at least 20 characters ({accessReason.trim().length}/20).</p>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveStatus} disabled={!accessReady || busy} data-testid="status-save-btn"
                  className={`${COMMIT} ${accessDraft === "active" ? "bg-olive-bright text-black hover:bg-olive" : "bg-amber-400 text-black hover:bg-amber-300"}`}>
                  {accessDraft === "disabled" ? "Disable account" : accessDraft === "archived" ? "Archive account" : accessDraft === "on_hold" ? "Put on hold" : "Reactivate"}
                </button>
                <button onClick={() => { setAccessDraft(curAccess); setAccessReason(""); }} data-testid="status-cancel-btn" className={`${BTN} border-sniper text-muted-foreground hover:text-foreground`}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="mt-1 font-mono text-[10px] text-muted-foreground" data-testid="status-help">Pick a different status to enable the change. Accounts are never deleted — records are always retained.</p>
          )}
        </div>
        {/* USERNAME OVERRIDE — admin sets a member's handle directly (bypasses cooldown). Audited.
            Moved here from the Verification card: it's an account-identity control, not verification. */}
        <div className="border-t border-sniper pt-3" data-testid="username-override">
          <div className="flex items-center gap-2 mb-2"><AtSign className="w-3.5 h-3.5 text-muted-foreground" /><span className="font-mono text-[10px] uppercase text-muted-foreground">Username override <span className="normal-case text-muted-foreground/70">(bypasses cooldown · audited)</span></span></div>
          <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase text-muted-foreground mb-2">Current <span className="text-foreground lowercase">@{detail.username}</span></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={uname} onChange={(e) => setUname(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="new_username" className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-full" data-testid="admin-username-input" />
            <input value={ureason} onChange={(e) => setUreason(e.target.value)} placeholder="Reason (required, audited)" className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-full" data-testid="admin-username-reason" />
          </div>
          <button onClick={overrideUsername} disabled={busy} data-testid="admin-username-save" className="mt-2 font-mono text-[10px] uppercase px-3 py-1.5 border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 disabled:opacity-40 transition-colors">Change username</button>
        </div>
        {/* Birthday override (super admin only, audited). Month/day only — never a year. */}
        {canSuper && (
          <div className="border-t border-sniper pt-3" data-testid="admin-birthday-override">
            <div className="flex items-center gap-2 mb-2"><Cake className="w-3.5 h-3.5 text-muted-foreground" /><span className="font-mono text-[10px] uppercase text-muted-foreground">Birthday <span className="normal-case text-muted-foreground/70">(month/day only · admin · audited)</span></span></div>
            <div className="flex items-center gap-2 mb-2">
              <select value={bmonth} onChange={(e) => setBmonth(e.target.value)} data-testid="admin-birthday-month"
                className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs">
                <option value="">Month —</option>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
              </select>
              <select value={bday} onChange={(e) => setBday(e.target.value)} data-testid="admin-birthday-day" disabled={!bmonth}
                className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs disabled:opacity-40">
                <option value="">Day —</option>
                {Array.from({ length: [31,29,31,30,31,30,31,31,30,31,30,31][(Number(bmonth) || 1) - 1] || 31 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <input value={breason} onChange={(e) => setBreason(e.target.value)} placeholder="Reason (required, audited)" data-testid="admin-birthday-reason"
              className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-full" />
            <button onClick={saveBirthday} disabled={busy} data-testid="admin-birthday-save"
              className="mt-2 font-mono text-[10px] uppercase px-3 py-1.5 border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 disabled:opacity-40 transition-colors">Save birthday</button>
          </div>
        )}
        {/* Legacy tombstoned accounts (from before deletion was removed) can still be restored. */}
        {canSuper && isReset && (
          <div className="border-t border-sniper pt-3" data-testid="danger-zone">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase text-muted-foreground">This account was tombstoned before deletion was removed.</span>
              <button onClick={restore} disabled={busy} data-testid="account-restore-btn"
                className={`${BTN} border-olive-bright/50 text-olive-bright hover:bg-olive/10 inline-flex items-center gap-1`}>
                <RotateCcw className="w-3 h-3" /> Restore
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VerificationCard({ detail, userId, onChanged }) {
  const [kind, setKind] = useState("ffl");
  const cur = kind === "ffl" ? detail.ffl : detail.sot;
  // FFL/SOT only apply to businesses/dealers. Individuals + ambassadors never see the license grant.
  const showLicense = detail.account_type === "business" || detail.account_type === "dealer";
  const isBusiness = detail.account_type === "business";
  const [form, setForm] = useState({ identifier: "", license_number: "", issuing_office: "", expires_at: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    const c = kind === "ffl" ? detail.ffl : detail.sot;
    setForm({
      identifier: (c && (c.type || c.class)) || "",
      license_number: c?.license_number || "",
      issuing_office: c?.issuing_office || "",
      expires_at: c?.expires_at ? String(c.expires_at).slice(0, 10) : "",
      note: c?.note || "",
    });
  }, [kind, detail.ffl, detail.sot]);

  const grant = async () => {
    if (!form.identifier.trim()) { toast.error(`Enter the ${kind.toUpperCase()} ${kind === "ffl" ? "type" : "class"}.`); return; }
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/license`, { kind, ...form });
      toast.success(`${kind.toUpperCase()} granted.`);
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const clearLic = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/license`, { kind, clear: true });
      toast.success(`${kind.toUpperCase()} cleared.`);
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unlockId = async () => {
    try {
      await api.post(`/admin/accounts/${userId}/identity-unlock`);
      toast.success("Identity verification unlocked.");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const resendEmail = async () => {
    setEmailBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/resend-email-verification`);
      toast.success(`Verification email sent to ${detail.email}.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not send verification email.");
    } finally { setEmailBusy(false); }
  };

  const inp = "bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-full";
  const expired = cur?.expires_at && new Date(cur.expires_at) < new Date();
  const emailVerified = detail.email_verified !== false;   // true OR grandfathered (missing)
  const taxToken = detail.identity_verified ? idTaxonomyChip(detail.id_taxonomy) : null;
  const provLabel = { seed: "seed fixture", internal: "internal / staff", unknown: "unknown origin", stripe: "Stripe webhook" }[detail.id_provenance];
  // Business verification: legacy business accounts predate the tracking field.
  const bizStatus = isBusiness
    ? (detail.business_verification_status || "unknown — predates tracking")
    : null;

  const ROW = "border-t border-sniper pt-3";
  const LABEL = "font-mono text-[10px] uppercase text-muted-foreground w-20 shrink-0";

  return (
    <div className="border border-sniper" data-testid="verification-card">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <FileCheck className="w-4 h-4 text-olive-bright" />
        <h3 className="font-heading uppercase tracking-tight text-foreground" data-testid="verification-card-title">Verification</h3>
      </div>
      <div className="p-3 space-y-3">
        {/* ROW 1 — EMAIL */}
        <div className="flex flex-wrap items-center gap-2.5" data-testid="verify-row-email">
          <span className={LABEL}>Email</span>
          <div data-testid="email-verify-controls" className="flex flex-wrap items-center gap-2.5">
            <StatusChip state={emailVerified ? "email_verified" : "not_started"} testId="verify-email-chip" />
            {emailVerified ? (
              <span className="font-mono text-[10px] text-muted-foreground" data-testid="email-verify-helper">
                Verified{detail.email_verified_at ? ` ${fmtJoined(detail.email_verified_at)}` : ""}
              </span>
            ) : (
              <>
                <button onClick={resendEmail} disabled={emailBusy} data-testid="resend-email-verification-btn"
                  className="font-mono uppercase text-[10px] font-bold px-3 py-1.5 bg-olive text-white hover:bg-olive-bright disabled:bg-sniper disabled:text-muted-foreground transition-colors">
                  {emailBusy ? "Sending…" : "Resend email verification"}
                </button>
                <span className="font-mono text-[10px] text-muted-foreground" data-testid="email-verify-last-sent">
                  {detail.email_verification_last_sent_at ? `Last sent ${fmtJoined(detail.email_verification_last_sent_at)}` : "Never sent"}
                </span>
              </>
            )}
          </div>
        </div>

        {/* ROW 2 — IDENTITY (+ taxonomy: is the flag actually Stripe-backed?) */}
        <div className={`${ROW} flex flex-wrap items-center gap-2.5`} data-testid="verify-row-identity">
          <span className={LABEL}>Identity</span>
          <StatusChip {...idChipState(detail)} testId="drawer-identity-chip" />
          {taxToken && <StatusChip state={taxToken} testId="drawer-id-taxonomy" />}
          {taxToken && provLabel && (
            <span className="font-mono text-[9px] uppercase text-muted-foreground" data-testid="drawer-id-provenance">
              {detail.id_taxonomy === "unbacked" ? `no Stripe backing · ${provLabel}` : provLabel}
            </span>
          )}
          <span className="font-mono text-[10px] uppercase text-muted-foreground">attempts {detail.identity_attempts}/3</span>
          {(detail.identity_locked || detail.identity_attempts > 0) && !detail.identity_verified && (
            <button onClick={unlockId} data-testid="identity-unlock-btn" className="font-mono text-[10px] uppercase px-3 py-1.5 border border-steel/60 text-steel hover:bg-steel/10 transition-colors inline-flex items-center gap-1"><Unlock className="w-3 h-3" /> Unlock &amp; reset</button>
          )}
          <RevokeVerificationButton userId={userId} detail={detail} onDone={onChanged} />
          {!detail.identity_verified && (
            <button onClick={async () => { try { await api.post(`/admin/accounts/${userId}/reverify-nudge`); toast.success("Re-verification prompt sent."); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } }}
              data-testid="reverify-nudge-btn"
              className="font-mono text-[10px] uppercase px-3 py-1.5 border border-[#d3a24e]/60 text-[#d3a24e] hover:bg-[#d3a24e]/10 transition-colors inline-flex items-center gap-1">
              <MailCheck className="w-3 h-3" /> Send re-verify prompt
            </button>
          )}
        </div>

        {/* ROW 2b — REVOKE / RE-VERIFY HISTORY (durable timeline; survives re-verify overwrite) */}
        {(detail.verification_history || []).some((h) => h.event === "revoked") && (
          <div className={ROW} data-testid="verify-row-history">
            <div className="flex flex-wrap items-center gap-2.5 mb-2"><span className={LABEL}>ID history</span></div>
            <ol className="space-y-1" data-testid="verify-history-list">
              {(detail.verification_history || []).map((h, i) => (
                <li key={i} className="font-mono text-[10px] uppercase text-muted-foreground flex flex-wrap items-center gap-1.5">
                  {h.event === "revoked"
                    ? <span className="text-blaze font-bold">Revoked</span>
                    : <span className="text-olive-bright font-bold">Re-verified</span>}
                  <span>{fmtJoined(h.at)}</span>
                  {h.by && <span className="normal-case">· by @{h.by}</span>}
                  {h.reason && <span className="normal-case text-secondary-foreground">· {h.reason}</span>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* ROW 3 — DEALER / FFL */}
        <div className={ROW} data-testid="verify-row-dealer">
          <div className="flex flex-wrap items-center gap-2.5 mb-2">
            <span className={LABEL}>Dealer / FFL</span>
            {detail.is_dealer
              ? <StatusChip state="dealer_verified" testId="verify-dealer-chip" />
              : <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="verify-dealer-none">{showLicense ? "no FFL on file" : "individual — n/a"}</span>}
          </div>
          {showLicense && (
          <div data-testid="license-ffl-block" className="pl-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex border border-sniper">
                {["ffl", "sot"].map((k) => (
                  <button key={k} onClick={() => setKind(k)} data-testid={`license-tab-${k}`}
                    className={`font-mono text-[10px] uppercase px-3 py-1.5 transition-colors ${kind === k ? "bg-olive text-white" : "text-muted-foreground hover:text-white"}`}>{k}</button>
                ))}
              </div>
              {cur && (
                <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${expired ? "border-blaze/50 text-blaze" : "border-teal-300/50 text-teal-300"}`} data-testid="license-current">
                  {kind.toUpperCase()} {cur.type || cur.class}{expired ? " · expired" : cur.expires_at ? ` · exp ${String(cur.expires_at).slice(0, 10)}` : ""}
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input value={form.identifier} onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))} placeholder={kind === "ffl" ? "FFL type (e.g. 01)" : "SOT class (2 or 3)"} className={inp} data-testid="license-identifier" />
              <input value={form.license_number} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} placeholder="License number" className={inp} data-testid="license-number" />
              <input value={form.issuing_office} onChange={(e) => setForm((f) => ({ ...f, issuing_office: e.target.value }))} placeholder="Issuing office" className={inp} data-testid="license-office" />
              <input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className={inp} data-testid="license-expires" />
              <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Admin note" className={`${inp} sm:col-span-2`} data-testid="license-note" />
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={grant} disabled={busy} data-testid="license-grant" className="font-mono text-[10px] uppercase px-3 py-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white transition-colors">Grant / update {kind.toUpperCase()}</button>
              {cur && <button onClick={clearLic} disabled={busy} data-testid="license-clear" className="font-mono text-[10px] uppercase px-3 py-1.5 border border-blaze/50 text-blaze hover:bg-blaze/10 transition-colors">Clear</button>}
            </div>
          </div>
          )}
        </div>

        {/* ROW 4 — BUSINESS */}
        <div className={`${ROW} flex flex-wrap items-center gap-2.5`} data-testid="verify-row-business">
          <span className={LABEL}>Business</span>
          {isBusiness ? (
            <>
              <StatusChip state={detail.business_verification_status === "verified" ? "active" : "pending"} testId="verify-business-chip" />
              <span className="font-mono text-[9px] uppercase text-muted-foreground" data-testid="verify-business-status">{bizStatus}</span>
              {detail.business_name && <span className="font-mono text-[10px] text-secondary-foreground">{detail.business_name}</span>}
            </>
          ) : (
            <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="verify-business-none">
              {detail.account_type === "ambassador" ? "ambassador account" : detail.account_type === "individual" ? "individual account" : "unclassified"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


function RevokeVerificationButton({ userId, detail, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [resetProfile, setResetProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const exempt = REVERIFY_EXEMPT_EMAILS.includes((detail.email || "").toLowerCase());
  if (!detail.identity_verified || exempt) return null;
  const ok = reason.trim().length >= 10;
  const submit = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/admin/accounts/${userId}/revoke-verification`, { reason: reason.trim(), reset_profile: resetProfile });
      toast.success(resetProfile
        ? "ID verification revoked + profile content cleared — listings untouched."
        : "ID verification revoked — badge removed, listings untouched.");
      setOpen(false); setReason(""); setResetProfile(false); onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="revoke-verification-open"
        className="font-mono text-[10px] uppercase px-3 py-1.5 border border-blaze/60 text-blaze hover:bg-blaze/10 transition-colors inline-flex items-center gap-1">
        <ShieldOff className="w-3 h-3" /> Revoke verification
      </button>
    );
  }
  return (
    <div className="w-full mt-2 border border-blaze/40 bg-blaze/5 p-3 space-y-2" data-testid="revoke-verification-panel">
      <p className="font-mono text-[10px] uppercase text-blaze leading-relaxed">Revoke ID verification — badge only, reversible. Listings stay live; the user re-verifies to post new ones.</p>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="revoke-verification-reason"
        placeholder="Reason (min 10 chars) — recorded in Audit Events"
        className="w-full bg-input border border-sniper focus:border-blaze outline-none px-2 py-1.5 text-foreground text-xs" />
      {!ok && <p className="text-[10px] text-blaze">Reason must be at least 10 characters ({reason.trim().length}/10).</p>}
      <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="revoke-reset-profile-row">
        <input type="checkbox" checked={resetProfile} onChange={(e) => setResetProfile(e.target.checked)}
          data-testid="revoke-reset-profile-check" className="mt-0.5 accent-blaze" />
        <span className="font-mono text-[10px] uppercase text-secondary-foreground leading-relaxed">
          Also reset profile content <span className="text-muted-foreground">(avatar → initials, bio, specialties, location, socials). Full start-over. Leave off for a badge-only revoke.</span>
        </span>
      </label>
      <div className="flex gap-2">
        <button onClick={submit} disabled={!ok || busy} data-testid="revoke-verification-confirm" className="font-mono text-[10px] uppercase px-3 py-1.5 border border-blaze/60 text-blaze hover:bg-blaze/10 disabled:opacity-40 disabled:cursor-not-allowed">{resetProfile ? "Revoke + reset profile" : "Revoke"}</button>
        <button onClick={() => { setOpen(false); setReason(""); setResetProfile(false); }} data-testid="revoke-verification-cancel" className="font-mono text-[10px] uppercase px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground">Cancel</button>
      </div>
    </div>
  );
}


// Profile photo cap (item 3). Super-admin override of an account's gallery limit. Default caps
// are role/type-derived (admins + ambassadors = 10, everyone else = 1); this lets a super admin
// raise/lower a single account. Lowering below the current count NEVER deletes photos — it blocks
// new uploads and flags the account over-limit. Backend enforces super-admin + audits every change.
function PhotoLimitControls({ detail, userId, onChanged }) {
  const { user: viewer } = useAuth();
  const canEdit = hasSuperPrivileges(viewer?.role);
  const hasOverride = detail.photo_limit_override != null;
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(hasOverride ? String(detail.photo_limit_override) : ""); }, [detail.photo_limit_override, hasOverride]);

  const save = async (limit) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/accounts/${userId}/photo-limit`, { limit });
      toast.success(limit == null
        ? `Override cleared — limit reverts to ${data.effective_limit}.`
        : `Photo limit set to ${data.effective_limit}.${data.photo_over_limit ? ` Account is now OVER (${data.photo_count}/${data.effective_limit}) — uploads blocked, no photos deleted.` : ""}`);
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not update photo limit."); }
    finally { setBusy(false); }
  };
  const apply = () => {
    const n = parseInt(val, 10);
    if (!Number.isInteger(n) || n < 1) { toast.error("Enter a whole number of 1 or more."); return; }
    save(n);
  };

  const count = detail.photo_count ?? 0;
  const eff = detail.photo_limit ?? 1;

  return (
    <div className="border border-sniper" data-testid="photo-limit-controls">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-olive-bright" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Profile Photos</h3>
      </div>
      <div className="p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase text-muted-foreground">Gallery</span>
          <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-sniper text-foreground" data-testid="photo-limit-count">{count} of {eff}</span>
          <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${hasOverride ? "border-olive-bright/50 text-olive-bright" : "border-sniper text-muted-foreground"}`} data-testid="photo-limit-source">
            {hasOverride ? "override" : "default cap"}
          </span>
          {detail.photo_over_limit && (
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-blaze/50 text-blaze inline-flex items-center gap-1" data-testid="photo-limit-over-warn">
              <AlertTriangle className="w-3 h-3" /> over limit — uploads blocked
            </span>
          )}
        </div>
        {canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[9px] uppercase text-muted-foreground">New limit (≥1)</span>
              <input type="number" min={1} value={val} onChange={(e) => setVal(e.target.value)} data-testid="photo-limit-input"
                placeholder={String(eff)} className="bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-28" />
            </label>
            <button onClick={apply} disabled={busy} data-testid="photo-limit-save"
              className="font-mono text-[10px] uppercase px-3 py-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white transition-colors">
              {busy ? "Saving…" : "Set limit"}
            </button>
            {hasOverride && (
              <button onClick={() => save(null)} disabled={busy} data-testid="photo-limit-clear"
                className="font-mono text-[10px] uppercase px-3 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">
                Clear override
              </button>
            )}
          </div>
        ) : (
          <p className="font-mono text-[9px] uppercase text-muted-foreground" data-testid="photo-limit-locked">Requires admin to change the photo cap.</p>
        )}
        <p className="font-mono text-[9px] uppercase text-muted-foreground/70 leading-relaxed">
          Lowering below the current count never deletes photos — it blocks new uploads and flags the account over-limit.
        </p>
      </div>
    </div>
  );
}


// Grant Audit View — the last N ADMIN credit grants on this account (actor, amount, reason,
// date). Grants only (not member purchases or spends), read from credit_balance_ledger. Gives
// the drawer a visible paper trail beside the store-credit balance.
function CreditGrantHistory({ detail }) {
  const grants = detail.credit_grants || [];
  return (
    <div className="border border-sniper" data-testid="credit-grants-controls">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <Gift className="w-4 h-4 text-volt" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Credit Grants</h3>
        <span className="ml-auto font-mono text-[10px] uppercase text-muted-foreground">Balance {fmtCredits(detail.credit_cents)}</span>
      </div>
      <div className="p-3">
        {grants.length === 0 ? (
          <p className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="credit-grants-empty">No admin credit grants on this account.</p>
        ) : (
          <ul className="space-y-2" data-testid="credit-grants-list">
            {grants.map((g, i) => (
              <li key={g.entry_id || i} className="flex items-start gap-2 border-b border-sniper/50 last:border-0 pb-2 last:pb-0" data-testid={`credit-grant-${i}`}>
                <span className="font-mono text-xs text-volt tabular-nums shrink-0" data-testid={`credit-grant-amount-${i}`}>+{fmtMoney(g.amount_cents)}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-foreground break-words" data-testid={`credit-grant-reason-${i}`}>{g.reason || <span className="text-muted-foreground italic">no reason recorded</span>}</div>
                  <div className="font-mono text-[9px] uppercase text-muted-foreground mt-0.5">
                    <span data-testid={`credit-grant-actor-${i}`}>{g.by || "—"}</span>
                    {g.created_at && <span data-testid={`credit-grant-date-${i}`}> · {new Date(g.created_at).toLocaleString()}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// Profile block at the top of the drawer. READ mode = plain text; EDIT mode = inputs for the
// admin-editable fields (name, email, phone) with SAVE / CANCEL. Email is super-admin only
// (auth-sensitive). The EDIT toggle lives in the drawer header (top-right, next to the X).

// Bulk RESEND EMAIL VERIFICATION — states the real count; already-verified are skipped server-side.
function BulkResendEmailModal({ rows, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const unverified = rows.filter((r) => r.email_verified === false);
  const verifiedCount = rows.length - unverified.length;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/bulk-resend-email-verification", {
        user_ids: rows.map((r) => r.user_id),
      });
      const parts = [`${data.sent} sent`];
      if (data.skipped_verified) parts.push(`${data.skipped_verified} already verified`);
      if (data.throttled) parts.push(`${data.throttled} throttled (rate limit)`);
      toast.success(`Email verification: ${parts.join(" · ")}.`);
      onDone?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not send.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="bulk-resend-email-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2"><MailCheck className="w-5 h-5 text-olive-bright" /> Resend email verification</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 font-mono text-xs">
          <p className="text-secondary-foreground" data-testid="bulk-resend-count">
            Send verification email to <span className="text-foreground font-bold">{unverified.length}</span> of {rows.length} selected users?
            {verifiedCount > 0 && <> <span className="text-muted-foreground">{verifiedCount} {verifiedCount === 1 ? "is" : "are"} already verified and will be skipped.</span></>}
          </p>
          {unverified.length === 0 && <p className="text-amber-400 uppercase text-[10px]">All selected users are already verified — nothing to send.</p>}
          <div className="flex items-center gap-2">
            <button onClick={run} disabled={busy || unverified.length === 0} data-testid="bulk-resend-confirm"
              className="font-mono uppercase text-[10px] font-bold px-4 py-1.5 bg-olive text-white hover:bg-olive-bright disabled:bg-sniper disabled:text-muted-foreground transition-colors">
              {busy ? "Sending…" : "Send verification emails"}
            </button>
            <button onClick={onClose} data-testid="bulk-resend-cancel" className="font-mono uppercase text-[10px] px-4 py-1.5 border border-sniper text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ProfileInfo({ detail, userId, editMode, setEditMode, canEditEmail, onChanged }) {
  const INP = "bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-mono text-xs w-full";
  const [first, setFirst] = useState(detail.first_name || "");
  const [last, setLast] = useState(detail.last_name || "");
  const [email, setEmail] = useState(detail.email || "");
  const [phone, setPhone] = useState(detail.phone || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFirst(detail.first_name || ""); setLast(detail.last_name || "");
    setEmail(detail.email || ""); setPhone(detail.phone || ""); setReason("");
  }, [detail, editMode]);

  const emailChanged = canEditEmail && email.trim().toLowerCase() !== (detail.email || "").toLowerCase();
  const dirty = first.trim() !== (detail.first_name || "") || last.trim() !== (detail.last_name || "")
    || phone.trim() !== (detail.phone || "") || emailChanged;
  const realName = [detail.first_name, detail.last_name].filter(Boolean).join(" ").trim() || (detail.real_name || "");

  const save = async () => {
    setBusy(true);
    try {
      const payload = { first_name: first.trim(), last_name: last.trim(), phone: phone.trim(), reason: reason.trim() };
      if (emailChanged) payload.email = email.trim();
      const { data } = await api.post(`/admin/accounts/${userId}/profile`, payload);
      toast.success(data.email_changed
        ? "Saved. Email changed — the member must re-verify and was signed out of all sessions."
        : "Profile saved.");
      setEditMode(false);
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!editMode) {
    return (
      <div className="grid sm:grid-cols-2 gap-3 font-mono text-xs" data-testid="account-profile-read">
        <div><span className="text-muted-foreground uppercase">Name</span><div className="text-foreground" data-testid="drawer-real-name">{realName || <span className="text-[#6B6D70] italic">{detail.identity_verified ? "— no name on file" : "name pending ID verification"}</span>}{detail.show_name_public && realName && <span className="ml-1 text-[8px] uppercase text-olive-bright/70 border border-olive-bright/30 px-1 not-italic">public</span>}</div></div>
        <div><span className="text-muted-foreground uppercase">Email</span><div className="text-foreground break-all" data-testid="drawer-email">{detail.email}{detail.email_verified === false && <span className="ml-1 text-[8px] uppercase text-[#d3a24e] border border-[#d3a24e]/40 px-1">unverified</span>}</div></div>
        <div><span className="text-muted-foreground uppercase">Role / Tier</span><div className="text-foreground uppercase">{detail.role}{detail.is_dealer ? ` · dealer:${detail.dealer_level || "standard"}` : detail.seller_tier ? ` · ${detail.seller_tier}` : ""}</div></div>
        <div><span className="text-muted-foreground uppercase">Status</span><div className="flex flex-wrap gap-1.5 mt-0.5">
          <StatusChip state={(detail.access_status || "active").toLowerCase()} testId="drawer-access-status" />
          {detail.banned && <span className="text-[10px] uppercase border border-blaze/50 text-blaze px-1.5 py-0.5">Banned</span>}
          {detail.is_dealer && <StatusChip state="dealer_verified" testId="drawer-dealer-chip" />}
          {detail.email_verified && <StatusChip state="email_verified" testId="drawer-email-chip" />}
          <StatusChip {...idChipState(detail)} testId="drawer-id-chip" />
          {detail.is_demo && <span className="text-[10px] uppercase border border-volt/50 text-volt px-1.5 py-0.5">Demo</span>}
        </div></div>
        <div><span className="text-muted-foreground uppercase">Activity</span><div className="text-foreground">{detail.listing_count} listings · {detail.orders_as_seller} sold-orders · {detail.orders_as_buyer} bought</div></div>
        <div><span className="text-muted-foreground uppercase">Phone</span><div className="text-foreground" data-testid="drawer-phone">{detail.phone || <span className="text-muted-foreground">—</span>}</div></div>
        <div><span className="text-muted-foreground uppercase">Birthday</span><div className="text-foreground" data-testid="drawer-birthday">{detail.birth_month && detail.birth_day ? `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][detail.birth_month - 1]} ${detail.birth_day}` : <span className="text-muted-foreground">—</span>}</div></div>
        <div><span className="text-muted-foreground uppercase">Store credit</span><div className="text-foreground" data-testid="drawer-credits">{fmtCredits(detail.credit_cents)}</div></div>
      </div>
    );
  }

  return (
    <div className="border border-olive/40 bg-olive/5 p-3 space-y-3" data-testid="account-profile-edit">
      <div className="grid sm:grid-cols-2 gap-2.5">
        <div><label className="font-mono text-[10px] uppercase text-muted-foreground block mb-1">First name</label><input value={first} onChange={(e) => setFirst(e.target.value)} maxLength={60} className={INP} data-testid="edit-first-name" /></div>
        <div><label className="font-mono text-[10px] uppercase text-muted-foreground block mb-1">Last name</label><input value={last} onChange={(e) => setLast(e.target.value)} maxLength={60} className={INP} data-testid="edit-last-name" /></div>
        <div className="sm:col-span-2"><label className="font-mono text-[10px] uppercase text-muted-foreground block mb-1">Email {canEditEmail ? "" : "· locked (super-admin accounts: super admin only)"}</label><input value={email} onChange={(e) => setEmail(e.target.value)} disabled={!canEditEmail} className={`${INP} ${!canEditEmail ? "opacity-60 cursor-not-allowed" : ""}`} data-testid="edit-email" /></div>
        <div><label className="font-mono text-[10px] uppercase text-muted-foreground block mb-1">Phone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} className={INP} data-testid="edit-phone" /></div>
        <div><label className="font-mono text-[10px] uppercase text-muted-foreground block mb-1">Reason (audited)</label><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (optional)" className={INP} data-testid="edit-reason" /></div>
      </div>
      {emailChanged && <p className="font-mono text-[10px] text-[#d3a24e] uppercase leading-relaxed" data-testid="edit-email-warning">Changing the email resets verification and signs the member out of every session.</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={busy || !dirty} data-testid="edit-save" className="font-mono text-[10px] uppercase px-4 py-2 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors">{busy ? "Saving…" : "Save changes"}</button>
        <button onClick={() => setEditMode(false)} data-testid="edit-cancel" className="font-mono text-[10px] uppercase px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
      </div>
    </div>
  );
}


function InvitesSection({ userId }) {
  const [tree, setTree] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/invites/tree/${userId}`).then(({ data }) => setTree(data)).catch(() => setTree({ error: true }));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const stageChip = (c) => {
    if (c.released_at) return { label: "Completed a deal", cls: "text-[#8FA84A] border-[#8FA84A]/40" };
    if (c.verified_at) return { label: "Verified", cls: "text-[#8FB8D8] border-[#8FB8D8]/40" };
    return { label: "Pending verification", cls: "text-muted-foreground border-sniper" };
  };
  const clusters = tree?.clusters || [];
  const invited = tree?.invited || [];
  const holds = tree?.holds || [];
  const stats = tree?.stats || {};
  const verifiedCount = stats.verified || 0;
  const pendingCount = Math.max(0, (stats.joined || 0) - verifiedCount);

  const resolveHold = async (holdId, action) => {
    try {
      await api.post(`/admin/invites/holds/${holdId}/${action}`, action === "reject" ? { reason: "admin review" } : {});
      toast.success(action === "release" ? "Attribution released" : "Hold rejected");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Action failed"); }
  };

  return (
    <div className="border border-sniper" data-testid="invites-section">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <Link2 className="w-4 h-4 text-olive-bright" />
        <span className="font-mono text-[11px] uppercase tracking-wide">Invites &amp; Referral</span>
        {tree && !tree.error && (
          <span className="ml-auto font-mono text-[10px] uppercase text-muted-foreground" data-testid="invites-stats">
            {verifiedCount} invited (verified) · {stats.joined || 0} signed up · {stats.released || 0} deals
          </span>
        )}
      </div>
      <div className="p-3 space-y-3">
        {!tree ? (
          <p className="font-mono text-[11px] uppercase text-muted-foreground" data-testid="invites-loading">Loading…</p>
        ) : tree.error ? (
          <p className="font-mono text-[11px] uppercase text-muted-foreground">Couldn't load invite data.</p>
        ) : (
          <>
            {/* Fraud-CLUSTER banner — the ring reads as a shape, not something to notice. */}
            {clusters.length > 0 && (
              <div className="border border-[#E5484D]/70 bg-[#E5484D]/12 p-3 space-y-2" data-testid="invites-clusters">
                <div className="flex items-center gap-1.5 text-[#F0575C] font-mono text-[11px] uppercase tracking-wide font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Suspicious cluster ({clusters.length})
                </div>
                {clusters.map((f, i) => (
                  <div key={i} className="text-[12px] font-body" data-testid={`invites-cluster-${f.type}`}>
                    <span className={`font-mono text-[10px] uppercase mr-1.5 ${f.severity === "high" ? "text-[#F0575C]" : "text-[#E8C46A]"}`}>[{f.severity}]</span>
                    <span className="text-foreground">{f.label}</span>
                    {f.members?.length > 0 && <span className="text-muted-foreground"> — {f.members.map((m) => `@${m}`).join(", ")}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Refused (held) signups — the attribution guard fired; nothing was bound. Reviewable. */}
            {holds.length > 0 && (
              <div className="border border-[#C2600F]/60 bg-[#C2600F]/10 p-3 space-y-2" data-testid="invites-holds">
                <div className="flex items-center gap-1.5 text-[#E8A05A] font-mono text-[11px] uppercase tracking-wide font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" /> Held for review ({holds.length})
                </div>
                <p className="text-[11px] font-body text-muted-foreground">Attribution refused (never bound). Release only if it's a genuine case, e.g. two people in one household.</p>
                {holds.map((h) => (
                  <div key={h.hold_id} className="flex items-center gap-2 text-[12px] font-body" data-testid={`invites-hold-${h.invitee_username || h.hold_id}`}>
                    <span className="flex-1 min-w-0 truncate text-foreground">@{h.invitee_username || "member"}
                      <span className="text-muted-foreground"> — {(h.reasons || []).join(", ")}</span>
                    </span>
                    <button onClick={() => resolveHold(h.hold_id, "release")} data-testid={`invites-hold-release-${h.hold_id}`}
                      className="shrink-0 font-mono text-[9px] uppercase px-1.5 py-0.5 border border-[#8FA84A]/50 text-[#8FA84A] hover:bg-[#8FA84A]/10">Release</button>
                    <button onClick={() => resolveHold(h.hold_id, "reject")} data-testid={`invites-hold-reject-${h.hold_id}`}
                      className="shrink-0 font-mono text-[9px] uppercase px-1.5 py-0.5 border border-blaze/50 text-blaze hover:bg-blaze/10">Reject</button>
                  </div>
                ))}
              </div>
            )}

            {/* Who invited THEM */}
            {tree.invited_by ? (
              <div className="flex items-center gap-2 text-[12.5px] font-body" data-testid="invites-invited-by">
                <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-muted-foreground uppercase font-mono text-[10px]">Invited by</span>
                <span className="text-foreground">@{tree.invited_by.username || "—"}</span>
                {tree.invited_by.suspicious && <span className="font-mono text-[9px] uppercase text-blaze border border-blaze/40 px-1">suspicious</span>}
                {tree.invited_by.status && <span className="font-mono text-[9px] uppercase text-muted-foreground">· {tree.invited_by.status}</span>}
              </div>
            ) : (
              <p className="text-[11px] font-mono uppercase text-muted-foreground" data-testid="invites-invited-by-none">Not invited by anyone (organic)</p>
            )}

            {/* Who THEY invited */}
            <div>
              <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1.5" data-testid="invites-children-count">
                {verifiedCount} invited (verified){pendingCount ? ` · ${pendingCount} pending verification` : ""}
              </p>
              {invited.length === 0 ? (
                <p className="text-[12px] font-body text-muted-foreground" data-testid="invites-children-empty">Hasn't invited anyone yet.</p>
              ) : (
                <div className="divide-y divide-sniper border border-sniper" data-testid="invites-children">
                  {invited.map((c) => {
                    const st = stageChip(c);
                    return (
                      <div key={c.attribution_id} className="flex items-center gap-2 px-2.5 py-2" data-testid={`invites-child-${c.username || c.user_id}`}>
                        <span className="flex-1 min-w-0 truncate text-[12.5px] text-foreground">@{c.username || "member"}</span>
                        {c.banned && <span className="font-mono text-[9px] uppercase text-blaze border border-blaze/40 px-1">banned</span>}
                        {c.suspicious && <span className="font-mono text-[9px] uppercase text-[#E8C46A] border border-[#E8C46A]/40 px-1" title={(c.suspicious_reasons || []).join(", ")}>flagged</span>}
                        <span className={`shrink-0 font-mono text-[9px] uppercase px-1.5 py-0.5 border ${st.cls}`}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UsernameHistory({ detail }) {
  const rows = detail.username_history || [];
  if (!rows.length) return null;
  const label = (r) => {
    if (r.changed_by === "creation" || !r.from_username) return "Set at account creation";
    if (r.forced) return r.actor ? `Username reset by admin (@${r.actor})` : "Username reset by admin";
    if (r.changed_by === "self") return "Changed by self via Settings & Security";
    if (r.changed_by === "admin") return r.actor ? `Changed by admin (@${r.actor})` : "Changed by admin";
    return "Handle changed";
  };
  return (
    <div className="border border-sniper" data-testid="username-history-card">
      <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
        <History className="w-4 h-4 text-olive-bright" />
        <h3 className="font-heading uppercase tracking-tight text-foreground">Username History</h3>
        <span className="ml-auto font-mono text-[10px] uppercase text-muted-foreground" data-testid="username-history-count">
          {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      <div className="divide-y divide-sniper">
        {rows.map((r, i) => {
          const isCurrent = i === 0;
          const creation = r.changed_by === "creation" || !r.from_username;
          return (
            <div key={r.history_id || i} data-testid={`username-history-row-${i}`}
              className={`px-3 py-2.5 flex items-start justify-between gap-3 ${isCurrent ? "bg-olive-bright/[0.06]" : ""}`}
              style={isCurrent ? { borderLeft: "2px solid #cfe86b" } : undefined}>
              <div className="min-w-0">
                <div className="font-mono text-sm flex items-center flex-wrap gap-x-2 gap-y-1">
                  {!creation && (
                    <>
                      <span className="text-muted-foreground line-through break-all" data-testid={`username-history-old-${i}`}>@{r.from_username}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    </>
                  )}
                  <span className={`break-all ${isCurrent ? "text-olive-bright" : "text-foreground"}`} data-testid={`username-history-new-${i}`}>@{r.to_username}</span>
                  {isCurrent && (
                    <span className="font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 border"
                      style={{ color: "#cfe86b", borderColor: "#cfe86b66", background: "rgba(207,232,107,0.08)" }}
                      data-testid={`username-history-current-${i}`}>Current</span>
                  )}
                </div>
                <div className="text-[11px] text-secondary-foreground uppercase tracking-wide mt-1 font-mono" data-testid={`username-history-by-${i}`}>
                  {label(r)}
                </div>
              </div>
              <div className="font-mono text-[10px] text-muted-foreground shrink-0 text-right tabular-nums" data-testid={`username-history-when-${i}`}>
                {r.changed_at ? new Date(r.changed_at).toLocaleString() : "—"}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-sniper font-mono text-[9px] uppercase tracking-wide text-muted-foreground/70">
        Append-only · admin only · never shown on the public profile
      </div>
    </div>
  );
}

function AccountDrawer({ userId, onClose }) {
  const { user: viewer } = useAuth();
  const canFlags = hasSuperPrivileges(viewer?.role);
  const [detail, setDetail] = useState(null);
  const [ip, setIp] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const loadDetail = useCallback(() => {
    api.get(`/admin/accounts/${userId}`).then(({ data }) => setDetail(data)).catch(() => {});
    api.get(`/admin/accounts/${userId}/ip-history`).then(({ data }) => setIp(data)).catch(() => {});
  }, [userId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const setFlag = async (key, val) => {
    try {
      await api.post(`/admin/accounts/${userId}/flags`, { [key]: val });
      toast.success(`${key === "is_test" ? "Test" : "Demo"} flag ${val ? "set" : "cleared"}`);
      loadDetail();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="account-drawer">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 text-olive-bright" /> {detail ? `@${detail.username}` : "Account"}
            {detail && <RoleTag account={detail} className="ml-1" />}
          </DialogTitle>
          {detail && !editMode && (
            <button onClick={() => setEditMode(true)} data-testid="drawer-edit-btn"
              className="absolute right-11 top-3.5 z-10 inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 transition-colors">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
        </DialogHeader>
        {!detail ? (
          <p className="font-mono text-sm text-muted-foreground uppercase" data-testid="account-drawer-loading">Loading…</p>
        ) : (
          <div className="space-y-5">
            <ProfileInfo detail={detail} userId={userId} editMode={editMode} setEditMode={setEditMode} canEditEmail={canFlags && (detail.role !== "super_admin" || isSuperAdmin(viewer?.role))} onChanged={loadDetail} />

            {/* Username History — append-only handle log (admin only) */}
            <UsernameHistory detail={detail} />

            {/* Data-hygiene flags — is_test hides from analytics + admin tables; is_demo excludes from analytics but stays publicly visible & marked. Super-admin only. */}
            {canFlags && !isAdminRole(detail.role) && (
              <div className="border border-sniper p-3 flex flex-wrap items-center gap-3" data-testid="account-data-flags">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">Data flags</span>
                <button onClick={() => setFlag("is_test", !detail.is_test)} data-testid="drawer-toggle-test"
                  className={`font-mono text-[10px] uppercase px-2 py-1 border transition-colors ${detail.is_test ? "text-foreground border-muted-foreground bg-white/5" : "text-muted-foreground border-sniper hover:border-white/40"}`}>
                  {detail.is_test ? "✓ Test account" : "Mark as test"}
                </button>
                <button onClick={() => setFlag("is_demo", !detail.is_demo)} data-testid="drawer-toggle-demo"
                  className={`font-mono text-[10px] uppercase px-2 py-1 border transition-colors ${detail.is_demo ? "text-amber-400 border-amber-400/60 bg-amber-400/10" : "text-muted-foreground border-sniper hover:border-white/40"}`}>
                  {detail.is_demo ? "✓ Demo account" : "Mark as demo"}
                </button>
                <span className="font-mono text-[9px] uppercase text-muted-foreground/70 ml-auto">test = hidden · demo = visible + analytics-excluded</span>
              </div>
            )}

            {/* Role / Tier / Lifecycle controls */}
            <AccountControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Account type classification (individual / business) */}
            <AccountTypeControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Ambassador account type + commercial terms (super admin only) */}
            <AmbassadorControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Beta Tester recognition + fee override (0% for 6 months, then admin-adjustable) */}
            <BetaTesterControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Membership — subscription status + comp / uncomp (super admin) */}
            <MembershipControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Credit grants — audit view of recent admin store-credit grants */}
            <CreditGrantHistory detail={detail} />

            {/* Consolidated Verification card — Email · Identity (+ taxonomy) · Dealer/FFL · Business */}
            <VerificationCard detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Invites & Referral — who invited them, who they invited, fraud-cluster flags */}
            <InvitesSection userId={userId} />

            {/* Profile photo cap — super-admin override (item 3) */}
            <PhotoLimitControls detail={detail} userId={userId} onChanged={loadDetail} />

            {/* Permissions Console */}
            <div className="border border-sniper" data-testid="permissions-console">
              <div className="px-3 py-2.5 border-b border-sniper flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-olive-bright" />
                <h3 className="font-heading uppercase tracking-tight text-foreground">Category Permissions</h3>
              </div>
              <div className="divide-y divide-sniper">
                {detail.permission_grid.map((item) => (
                  <PermissionRow key={item.category} uid={userId} item={item}
                    onChanged={(grid) => { setDetail((d) => ({ ...d, permission_grid: grid })); }} />
                ))}
              </div>
            </div>

            {/* Linked accounts (account switcher) */}
            <LinkedAccounts userId={userId} username={detail.username} />

            {/* IP / Login history */}
            <div className="border border-sniper" data-testid="account-ip-history">
              <div className="px-3 py-2.5 border-b border-sniper flex items-center justify-between">
                <h3 className="font-heading uppercase tracking-tight text-foreground flex items-center gap-2"><Fingerprint className="w-4 h-4 text-olive-bright" /> IP / Login History</h3>
                <span className="font-mono text-[10px] uppercase text-muted-foreground">{ip?.distinct_ip_count ?? 0} distinct IPs</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-xs">
                  <thead><tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
                    <th className="px-3 py-2">Result</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">When</th>
                  </tr></thead>
                  <tbody className="divide-y divide-sniper">
                    {(ip?.login_attempts || []).length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-4 text-muted-foreground">No login attempts logged yet (logging starts at ship time).</td></tr>
                    )}
                    {(ip?.login_attempts || []).slice(0, 20).map((a) => (
                      <tr key={a.attempt_id} className="text-secondary-foreground" data-testid="ip-history-row">
                        <td className="px-3 py-2">{a.success ? <span className="text-olive-bright">success</span> : <span className="text-blaze">failed</span>}</td>
                        <td className="px-3 py-2 text-foreground">{a.ip || "—"}{a.is_vpn && <span className="ml-1 text-[9px] uppercase text-blaze border border-blaze/50 px-1">VPN</span>}</td>
                        <td className="px-3 py-2">{a.country_name || a.country_code || "Unknown"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IpReport() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/ip-report").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <p className="font-mono text-sm text-muted-foreground uppercase mt-4">Loading…</p>;
  return (
    <div className="mt-4" data-testid="ip-report">
      <div className="border border-volt/40 bg-volt/5 p-3 mb-4 font-mono text-[11px] uppercase text-secondary-foreground flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-volt mt-0.5 shrink-0" /> {data.note}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Attempts", data.totals.attempts], ["Failures", data.totals.failures], ["Accounts seen", data.totals.accounts_seen], ["Anon failures", data.totals.anon_failures]].map(([l, v]) => (
          <div key={l} className="border border-sniper bg-surface p-4"><div className="font-mono text-[10px] uppercase text-muted-foreground">{l}</div><div className="font-heading text-2xl mt-1">{v}</div></div>
        ))}
      </div>
      <div className="border border-sniper bg-surface mt-4 overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead><tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
            <th className="px-3 py-2">Account</th><th className="px-3 py-2">Attempts</th><th className="px-3 py-2">Failures</th><th className="px-3 py-2">Distinct IPs</th><th className="px-3 py-2">Last</th>
          </tr></thead>
          <tbody className="divide-y divide-sniper">
            {data.accounts.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-muted-foreground">No login activity logged yet.</td></tr>}
            {data.accounts.map((r) => (
              <tr key={r.user_id} className="text-secondary-foreground" data-testid="ip-report-row">
                <td className="px-3 py-2 text-foreground"><span className="inline-flex items-center gap-2">@{r.username} <RoleTag account={r} />{r.is_demo && <span className="text-[9px] uppercase text-volt border border-volt/50 px-1">demo</span>}</span></td>
                <td className="px-3 py-2">{r.attempts}</td>
                <td className={`px-3 py-2 ${r.failures ? "text-blaze" : ""}`}>{r.failures}</td>
                <td className="px-3 py-2">{r.distinct_ips}{r.multi_ip && <span className="ml-1 text-[9px] uppercase text-volt border border-volt/50 px-1">multi</span>}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.last_attempt ? new Date(r.last_attempt).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkDeleteDialog({ ids, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/admin/accounts/bulk-delete", { account_ids: ids, dry_run: true })
      .then(({ data }) => setPreview(data)).catch((err) => toast.error(formatError(err.response?.data?.detail)));
  }, [ids]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/bulk-delete", { account_ids: ids, confirmation_phrase: phrase, reason: "beta account delete" });
      toast.success(`${data.deleted_count} deleted · ${data.skipped_count} skipped`);
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const needPhrase = preview?.confirmation_phrase || "DELETE ACCOUNTS";
  const ready = phrase.trim() === needPhrase && (preview?.deletable_count || 0) > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-blaze/40 text-foreground max-w-lg" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-blaze">
            <Trash2 className="w-5 h-5" /> Delete accounts
          </DialogTitle>
        </DialogHeader>
        {!preview ? (
          <p className="font-mono text-sm text-muted-foreground uppercase">Loading preview…</p>
        ) : (
          <div className="space-y-4">
            <p className="font-mono text-xs text-secondary-foreground">
              Soft-delete (tombstone): each account's email &amp; username are freed for re-registration. Reversible via Restore.
            </p>
            <div className="border border-sniper p-3 space-y-1 max-h-48 overflow-y-auto">
              <p className="font-mono text-[10px] uppercase text-olive-bright" data-testid="bulk-deletable-count">Will delete: {preview.deletable_count}</p>
              {preview.deletable.map((a) => (
                <div key={a.user_id} className="font-mono text-xs text-foreground">@{a.username} <span className="text-muted-foreground">{a.email}</span></div>
              ))}
              {preview.skipped_count > 0 && (
                <>
                  <p className="font-mono text-[10px] uppercase text-amber-400 mt-2" data-testid="bulk-skipped-count">Skipped: {preview.skipped_count}</p>
                  {preview.skipped.map((s, i) => (
                    <div key={i} className="font-mono text-xs text-muted-foreground">@{s.username || s.user_id} — {s.reason}</div>
                  ))}
                </>
              )}
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-muted-foreground">Type <span className="text-blaze">{needPhrase}</span> to confirm</label>
              <input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="bulk-delete-phrase"
                className="w-full mt-1 bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground font-mono text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={confirm} disabled={!ready || busy} data-testid="bulk-delete-confirm"
                className="font-mono uppercase text-[10px] px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Delete {preview.deletable_count} account{preview.deletable_count === 1 ? "" : "s"}
              </button>
              <button onClick={onClose} className="font-mono uppercase text-[10px] px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminCleanupDialog({ onClose, onDone }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.post("/admin/admin-cleanup/dry-run", {})
      .then(({ data }) => setReport(data))
      .catch((err) => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const phraseOk = report && phrase.trim() === report.confirmation_phrase;
  const blocked = report && (report.counts.admins_remaining_after === 0);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/admin-cleanup/apply", { confirmation_phrase: phrase.trim() });
      toast.success(`${data.demoted} demoted to comped Elite · ${data.skipped} skipped`);
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-[#d3a24e]/40 text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-[#d3a24e]">
            <Gift className="w-5 h-5" /> Demote admins → comped Elite
          </DialogTitle>
        </DialogHeader>
        {loading || !report ? (
          <p className="font-mono text-sm text-muted-foreground uppercase" data-testid="admin-cleanup-loading">Running dry run…</p>
        ) : (
          <div className="space-y-4">
            <p className="font-mono text-xs text-secondary-foreground leading-relaxed">
              Every admin/super below (except the exempt owner accounts) becomes a <strong>user</strong> with a
              complimentary <strong>Elite</strong> seat (excluded from revenue). Sessions are revoked and each gets a friendly email.
            </p>
            {(report.warnings || []).map((w, i) => (
              <p key={i} className="font-mono text-[11px] text-blaze border border-blaze/40 p-2" data-testid={`admin-cleanup-warning-${i}`}>⚠ {w}</p>
            ))}
            <div className="border border-[#d3a24e]/30 p-2 max-h-52 overflow-y-auto" data-testid="admin-cleanup-would-demote">
              <p className="font-mono text-[10px] uppercase text-[#d3a24e] mb-1">Would demote ({report.counts.would_demote})</p>
              {report.would_demote.map((a) => (
                <div key={a.user_id} className="font-mono text-xs text-foreground">@{a.username} <span className="text-muted-foreground">{a.email} · {a.role} · {a.current_tier}</span>{a.is_permanent && <span className="text-blaze"> · PERMANENT</span>}</div>
              ))}
            </div>
            <div className="border border-sniper p-2" data-testid="admin-cleanup-exempt">
              <p className="font-mono text-[10px] uppercase text-teal-300 mb-1">Exempt — keep admin ({report.counts.exempt})</p>
              {report.exempt.length === 0 && <p className="font-mono text-xs text-muted-foreground">None found in this environment.</p>}
              {report.exempt.map((a) => (
                <div key={a.user_id} className="font-mono text-xs text-foreground">@{a.username} <span className="text-muted-foreground">{a.email} · {a.role}</span></div>
              ))}
            </div>
            {blocked ? (
              <p className="font-mono text-xs text-blaze" data-testid="admin-cleanup-blocked">Apply is blocked — zero admins would remain. Run on production where the exempt owner accounts exist.</p>
            ) : (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted-foreground">Type <span className="text-[#d3a24e]">{report.confirmation_phrase}</span> to confirm</label>
                  <input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="admin-cleanup-phrase"
                    className="w-full mt-1 bg-input border border-sniper focus:border-[#d3a24e] outline-none px-3 py-2 text-foreground font-mono text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={apply} disabled={!phraseOk || busy} data-testid="admin-cleanup-confirm"
                    className="font-mono uppercase text-[10px] px-4 py-2 border border-[#d3a24e]/60 text-[#d3a24e] hover:bg-[#d3a24e]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? "Working…" : `Demote ${report.counts.would_demote} → comped Elite`}
                  </button>
                  <button onClick={onClose} data-testid="admin-cleanup-cancel" className="font-mono uppercase text-[10px] px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Bulk "Request ID verification" — dry-run FIRST (shows who is contacted / skipped),
// then applies through the re-verification campaign machinery on confirm.
function RequestVerifyModal({ ids, onClose, onDone }) {
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/admin/reverify-campaign/bulk-request", { user_ids: ids, dry_run: true })
      .then(({ data }) => setDry(data))
      .catch((e) => { toast.error(formatError(e.response?.data?.detail)); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reverify-campaign/bulk-request", { user_ids: ids, dry_run: false });
      toast.success(`Sent ${data.sent} verification request${data.sent === 1 ? "" : "s"}.`);
      onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const skippedTotal = dry ? dry.requested - dry.will_contact_count : 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-verify-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2"><MailCheck className="w-5 h-5 text-olive-bright" /> Request ID verification</DialogTitle>
        </DialogHeader>
        {!dry ? (
          <p className="font-mono text-sm text-muted-foreground uppercase" data-testid="request-verify-loading">Checking who gets contacted…</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-secondary-foreground">Runs through the re-verification campaign — a scoped campaign is created and wave 0 emailed. Nothing sends until you confirm.</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-olive/40 p-2"><div className="font-heading text-2xl text-olive-bright" data-testid="rv-will-contact">{dry.will_contact_count}</div><div className="font-mono text-[9px] uppercase text-muted-foreground">Will be contacted</div></div>
              <div className="border border-sniper p-2"><div className="font-heading text-2xl text-muted-foreground" data-testid="rv-skipped">{skippedTotal}</div><div className="font-mono text-[9px] uppercase text-muted-foreground">Skipped</div></div>
            </div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
              {Object.entries(dry.per_segment).map(([s, n]) => n > 0 && <span key={s}>{s.replace(/_/g, " ")}: <span className="text-foreground">{n}</span></span>)}
            </div>
            {dry.will_contact.length > 0 && (
              <div className="border border-olive/30 p-2 max-h-40 overflow-y-auto" data-testid="rv-list-contact">
                <p className="font-mono text-[10px] uppercase text-olive-bright mb-1">Will be contacted</p>
                {dry.will_contact.map((w) => <div key={w.user_id} className="font-mono text-xs text-foreground">@{w.username} <span className="text-muted-foreground">· {w.email} · {String(w.segment).replace(/_/g, " ")}</span></div>)}
              </div>
            )}
            {Object.values(dry.skipped).some((v) => v.length > 0) && (
              <div className="border border-sniper p-2 max-h-32 overflow-y-auto" data-testid="rv-list-skipped">
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Skipped</p>
                {Object.entries(dry.skipped).map(([reason, arr]) => arr.map((w) => <div key={w.user_id} className="font-mono text-xs text-muted-foreground">@{w.username} — {reason.replace(/_/g, " ")}</div>))}
              </div>
            )}
            {!dry.sender_ok && <p className="font-mono text-[11px] text-blaze" data-testid="rv-sender-warn">Sending blocked: SENDER_EMAIL is not the required address.</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={send} disabled={busy || dry.will_contact_count === 0 || !dry.sender_ok} data-testid="rv-confirm-btn"
                className="flex-1 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading uppercase py-2.5 transition-colors">
                {busy ? "Sending…" : `Send ${dry.will_contact_count} request${dry.will_contact_count === 1 ? "" : "s"}`}
              </button>
              <button onClick={onClose} data-testid="rv-cancel-btn" className="px-4 border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-sm">Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function PurgeDialog({ mode, ids, onClose, onDone }) {
  const [report, setReport] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const body = mode === "disabled" ? { include_all_disabled: true } : { account_ids: ids };

  useEffect(() => {
    setLoading(true);
    api.post("/admin/accounts/purge/dry-run", body)
      .then(({ data }) => setReport(data))
      .catch((err) => toast.error(formatError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phraseOk = report && phrase.trim() === report.confirmation_phrase;
  const total = report ? report.counts.purgeable + report.counts.anonymize_instead : 0;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/purge/apply",
        { ...body, confirmation_phrase: phrase.trim(), reason: "manual account purge" });
      toast.success(`${data.deleted} deleted · ${data.anonymized} anonymized · ${data.excluded} excluded`);
      onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-blaze/40 text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="purge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-blaze">
            <Trash2 className="w-5 h-5" /> Purge accounts {mode === "disabled" ? "(all disabled)" : `(${ids.length} selected)`}
          </DialogTitle>
        </DialogHeader>
        {loading || !report ? (
          <p className="font-mono text-sm text-muted-foreground uppercase" data-testid="purge-dry-loading">Running dry run…</p>
        ) : (
          <div className="space-y-4">
            <p className="font-mono text-xs text-secondary-foreground leading-relaxed">
              Hard-delete is permanent. Accounts with financial history (orders, transfers, payouts, disputes,
              Stripe Connect, membership, credit purchases, paid boosts) are <span className="text-amber-400">anonymized</span> instead —
              their records survive. Purgeable accounts have their listings removed with them.
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-blaze/40 p-2"><div className="font-heading text-2xl text-blaze" data-testid="purge-count-purgeable">{report.counts.purgeable}</div><div className="font-mono text-[9px] uppercase text-muted-foreground">Hard delete</div></div>
              <div className="border border-amber-400/40 p-2"><div className="font-heading text-2xl text-amber-400" data-testid="purge-count-anonymize">{report.counts.anonymize_instead}</div><div className="font-mono text-[9px] uppercase text-muted-foreground">Anonymize</div></div>
              <div className="border border-sniper p-2"><div className="font-heading text-2xl text-muted-foreground" data-testid="purge-count-excluded">{report.counts.excluded}</div><div className="font-mono text-[9px] uppercase text-muted-foreground">Excluded</div></div>
            </div>
            <p className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="purge-owned-listings">Owned listings to remove: <span className="text-foreground">{report.owned_listings_affected}</span></p>

            {report.purgeable.length > 0 && (
              <div className="border border-blaze/30 p-2 max-h-40 overflow-y-auto" data-testid="purge-list-purgeable">
                <p className="font-mono text-[10px] uppercase text-blaze mb-1">Hard delete ({report.purgeable.length})</p>
                {report.purgeable.map((a) => (
                  <div key={a.user_id} className="font-mono text-xs text-foreground">@{a.username} <span className="text-muted-foreground">{a.email} · {a.listing_count} listing(s)</span></div>
                ))}
              </div>
            )}
            {report.anonymize_instead.length > 0 && (
              <div className="border border-amber-400/30 p-2 max-h-40 overflow-y-auto" data-testid="purge-list-anonymize">
                <p className="font-mono text-[10px] uppercase text-amber-400 mb-1">Anonymize instead ({report.anonymize_instead.length})</p>
                {report.anonymize_instead.map((a) => (
                  <div key={a.user_id} className="font-mono text-xs text-foreground">@{a.username} <span className="text-muted-foreground">— {a.anonymize_reasons.join(", ")}</span></div>
                ))}
              </div>
            )}
            {report.excluded.length > 0 && (
              <div className="border border-sniper p-2 max-h-32 overflow-y-auto" data-testid="purge-list-excluded">
                <p className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Excluded ({report.excluded.length})</p>
                {report.excluded.map((a) => (
                  <div key={a.user_id} className="font-mono text-xs text-muted-foreground">@{a.username} — {a.excluded_reason}</div>
                ))}
              </div>
            )}

            {total > 0 ? (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase text-muted-foreground">Type <span className="text-blaze">{report.confirmation_phrase}</span> to confirm</label>
                  <input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="purge-phrase-input"
                    className="w-full mt-1 bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground font-mono text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={apply} disabled={!phraseOk || busy} data-testid="purge-confirm-btn"
                    className="font-mono uppercase text-[10px] px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? "Purging…" : `Purge — delete ${report.counts.purgeable}, anonymize ${report.counts.anonymize_instead}`}
                  </button>
                  <button onClick={onClose} data-testid="purge-cancel-btn" className="font-mono uppercase text-[10px] px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                </div>
              </>
            ) : (
              <p className="font-mono text-xs text-muted-foreground" data-testid="purge-nothing">Nothing to purge in this selection.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlatformReverifyModal({ open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setData(null); setReason(""); setSel(new Set());
    api.get("/admin/accounts/revoke-verification/preview")
      .then(({ data }) => { setData(data); setSel(new Set((data.accounts || []).map((a) => a.user_id))); })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (uid) => setSel((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const reasonOk = reason.trim().length >= 10;
  const count = sel.size;

  const apply = async () => {
    if (!reasonOk || count === 0) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/accounts/revoke-verification/bulk", { reason: reason.trim(), user_ids: [...sel] });
      toast.success(`Revoked ${res.revoked} account(s). Batch ${res.batch_id}.`);
      onClose(); onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-surface border-sniper" data-testid="reverify-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2"><ShieldOff className="w-5 h-5 text-blaze" /> Platform-wide ID re-verification</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="font-mono text-xs text-muted-foreground py-6 text-center">Loading affected accounts…</p>
        ) : data ? (
          <div className="space-y-3">
            <p className="font-mono text-[11px] text-secondary-foreground leading-relaxed">
              Dry run — nothing changes until you click Revoke. This resets each account's Verified ID badge and forces re-verification before they can post new listings. Existing listings stay live. Reversible.
            </p>
            <div className="font-mono text-[10px] uppercase text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span data-testid="reverify-total">Would affect: <span className="text-foreground">{data.count}</span></span>
              <span>Selected: <span className="text-foreground" data-testid="reverify-selected">{count}</span></span>
              <span>Protected (exempt): <span className="text-teal-300">{data.exempt_count}</span></span>
            </div>
            <div className="border border-sniper max-h-[40vh] overflow-y-auto divide-y divide-sniper" data-testid="reverify-list">
              {(data.accounts || []).map((a) => (
                <label key={a.user_id} className="flex items-center gap-2 px-3 py-2 font-mono text-[11px] cursor-pointer hover:bg-white/5" data-testid={`reverify-row-${a.username}`}>
                  <input type="checkbox" checked={sel.has(a.user_id)} onChange={() => toggle(a.user_id)} className="accent-blaze w-4 h-4" data-testid={`reverify-check-${a.username}`} />
                  <span className="text-foreground truncate flex-1">@{a.username} <span className="text-muted-foreground">· {a.email}</span></span>
                  {a.is_demo && <span className="text-[9px] uppercase text-volt border border-volt/50 px-1">demo</span>}
                  {a.is_test && <span className="text-[9px] uppercase text-steel border border-steel/50 px-1">test</span>}
                  {isAdminRole(a.role) && <span className="text-[9px] uppercase text-blaze border border-blaze/50 px-1">{a.role}</span>}
                </label>
              ))}
              {(data.accounts || []).length === 0 && <p className="px-3 py-4 text-muted-foreground font-mono text-[11px]">No verified accounts to revoke (all are exempt or none are verified).</p>}
            </div>
            {data.exempt_count > 0 && (
              <p className="font-mono text-[10px] text-teal-300/80" data-testid="reverify-exempt-note">
                Protected & never touched: {(data.exempt_emails || []).join(", ")}
              </p>
            )}
            <div>
              <label className="block font-mono text-[10px] uppercase text-secondary-foreground mb-1">Reason — applies to the whole batch (min 10 chars, recorded per account in Audit Events)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="reverify-reason"
                placeholder="e.g. Platform-wide beta re-verification — resetting all ID badges before general launch"
                className="w-full bg-input border border-sniper focus:border-blaze outline-none px-2 py-1.5 text-foreground text-xs" />
              {!reasonOk && <p className="text-[10px] text-blaze">Reason must be at least 10 characters ({reason.trim().length}/10).</p>}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose} data-testid="reverify-cancel" className="font-mono uppercase text-[10px] px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground">Cancel</button>
              <button onClick={apply} disabled={!reasonOk || count === 0 || busy} data-testid="reverify-apply"
                className="font-mono uppercase text-[10px] px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 font-bold disabled:opacity-40 disabled:cursor-not-allowed">
                Revoke {count} account{count === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        ) : (
          <p className="font-mono text-xs text-muted-foreground py-6 text-center">Could not load the account list.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- Faceted checkbox filter rail (mirrors the /feed rail) ----
const railSlug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function CountPill({ n }) {
  if (n == null) return null;
  return <span className={`ml-auto font-mono text-[10px] tabular-nums ${n > 0 ? "text-muted-foreground" : "text-muted-foreground/40"}`}>{n}</span>;
}

function RailGroup({ label, children }) {
  return (
    <div className="mb-5">
      <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2">{label}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function OptionRow({ testid, active, label, count, onClick, indent }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2 ${indent ? "pl-6 pr-2" : "px-2"} py-1.5 text-left text-sm transition-colors border ${
        active
          ? "border-olive-bright bg-olive/10 text-foreground"
          : "border-transparent hover:border-sniper text-secondary-foreground hover:text-foreground"
      }`}
    >
      {indent && <span aria-hidden className="text-muted-foreground/40 -ml-3 mr-0.5">└</span>}
      <span className={`inline-block w-3 h-3 border ${active ? "border-olive-bright bg-olive-bright" : "border-sniper"} shrink-0`} />
      <span className={`truncate ${indent ? "text-muted-foreground" : ""}`}>{label}</span>
      <CountPill n={count} />
    </button>
  );
}

// Group display order + headings for the accounts rail. `data` (demo/test toggles) is handled last.
const ACCT_FILTER_GROUPS = [
  { id: "status", label: "Status" },
  { id: "account_type", label: "Account type" },
  { id: "role", label: "Role" },
  { id: "verification", label: "Verification" },
  { id: "payout", label: "Payout" },
  { id: "tier", label: "Tier" },
];

// Pagination control — rendered at the TOP and BOTTOM of the table. Summary ("Showing 1–25 of 31")
// is always accurate to the FULL filtered set (total), never the current page. Per-page + page live
// in the URL (parent owns state). "All" removes paging (single request, high server limit).
function Pager({ page, perPage, total, onPage, onPerPage, testid }) {
  const isAll = perPage === "all";
  const size = isAll ? Math.max(total, 1) : perPage;
  const pageCount = Math.max(1, Math.ceil((total || 0) / size));
  const cur = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (isAll ? 1 : (cur - 1) * size + 1);
  const to = isAll ? total : Math.min(cur * size, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] uppercase text-muted-foreground tabular-nums" data-testid={`${testid}-summary`}>
          Showing {from}&ndash;{to} of {total}
        </span>
        <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground">
          <span className="hidden sm:inline">Per page</span>
          <select value={String(perPage)} onChange={(e) => onPerPage(e.target.value === "all" ? "all" : Number(e.target.value))}
            data-testid={`${testid}-perpage`}
            className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1 text-foreground font-mono text-[11px]">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            <option value="all">All</option>
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(cur - 1)} disabled={isAll || cur <= 1} data-testid={`${testid}-prev`}
          className="inline-flex items-center gap-1 border border-sniper px-2.5 py-1.5 font-mono uppercase text-[10px] text-secondary-foreground hover:text-foreground hover:border-olive disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" /> Prev
        </button>
        <span className="font-mono text-[10px] uppercase text-muted-foreground px-2 tabular-nums" data-testid={`${testid}-page`}>Page {cur} / {pageCount}</span>
        <button onClick={() => onPage(cur + 1)} disabled={isAll || cur >= pageCount} data-testid={`${testid}-next`}
          className="inline-flex items-center gap-1 border border-sniper px-2.5 py-1.5 font-mono uppercase text-[10px] text-secondary-foreground hover:text-foreground hover:border-olive disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


export default function AdminAccounts() {
  const { user: viewer } = useAuth();
  const canBulk = hasSuperPrivileges(viewer?.role); // super admins select rows to PURGE (hard delete / anonymize)
  const [tab, setTab] = useState("accounts");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [adminCount, setAdminCount] = useState(0);
  const [admins, setAdmins] = useState([]);
  const [search, setSearch] = useState(() => { try { return new URLSearchParams(window.location.search).get("search") || ""; } catch { return ""; } });
  // Multi-select facet groups (OR within a group, AND across groups). STATUS defaults to
  // "active" — what an admin wants ~95% of the time; Disabled/Archived are one click away.
  const DEFAULT_FILTERS = { status: ["active"], account_type: [], role: [], verification: [], payout: [], tier: [] };
  // Hydrate ALL filter/pagination state from the URL on first mount so a bookmarked / shared /
  // saved-search URL applies exactly, and Back/Forward work.
  const _u0 = (() => { try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); } })();
  const _csv = (k) => (_u0.get(k) ? _u0.get(k).split(",").filter(Boolean) : []);
  const [debouncedSearch, setDebouncedSearch] = useState(() => (_u0.get("search") || "").trim());
  const [filters, setFilters] = useState(() => ({
    status: _u0.has("status") ? _csv("status") : ["active"],
    account_type: _csv("account_type"),
    role: _csv("role"),
    verification: _csv("verification"),
    payout: _csv("payout"),
    tier: _csv("tier"),
  }));
  const [dataFlags, setDataFlags] = useState(() => ({ show_demo: _u0.get("show_demo") === "true" }));
  const [facets, setFacets] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [unclassified, setUnclassified] = useState(0);
  const [sortRole, setSortRole] = useState(null); // null | "asc" | "desc"
  const [sortTier, setSortTier] = useState(null); // null | "asc" | "desc"
  const [sort, setSort] = useState(() => _u0.get("sort") || "newest"); // newest | oldest (server-side)
  // ---- Pagination (Item 10) — page + per-page both live in the URL ----
  const [perPage, setPerPage] = useState(() => {
    const pp = _u0.get("per_page");
    if (pp === "all") return "all";
    const n = Number(pp);
    return [25, 50, 100].includes(n) ? n : 25;
  });
  const [page, setPage] = useState(() => Math.max(1, Number(_u0.get("page")) || 1));
  const [fetching, setFetching] = useState(false);
  const [memSummary, setMemSummary] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [purge, setPurge] = useState(null); // {mode:'selected'|'disabled', ids:[]}
  const [reverifyOpen, setReverifyOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [verifyReq, setVerifyReq] = useState(null);      // { ids:[...] } | null
  const [resendReq, setResendReq] = useState(null);      // { rows:[...] } | null
  const [classifyMenu, setClassifyMenu] = useState(false);
  const [bulkMore, setBulkMore] = useState(false);
  const [bulkPurgeEnabled, setBulkPurgeEnabled] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // mobile rail toggle
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem("os_admin_accounts_filters_collapsed") === "1"; } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem("os_admin_accounts_filters_collapsed", next ? "1" : "0"); } catch {}
    return next;
  });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set("search", debouncedSearch);
    ["status", "account_type", "role", "verification", "payout", "tier"].forEach((g) => {
      if (filters[g] && filters[g].length) p.set(g, filters[g].join(","));
    });
    if (dataFlags.show_demo) p.set("show_demo", "true");
    if (sort) p.set("sort", sort);
    const isAll = perPage === "all";
    const limit = isAll ? 5000 : perPage;
    const skip = isAll ? 0 : (page - 1) * perPage;
    p.set("limit", String(limit));
    p.set("skip", String(skip));
    setFetching(true);
    api.get(`/admin/accounts?${p.toString()}`).then(({ data }) => {
      setRows(data.accounts); setTotal(data.total);
      setFacets(data.facets || {});
      setFilterOptions(data.filter_options || {});
      setAdminCount(data.admin_count || 0);
      setUnclassified(data.unclassified_count || 0);
      setBulkPurgeEnabled(!!data.bulk_purge_enabled);
      setAdmins(data.admins || []);
    }).catch(() => {}).finally(() => setFetching(false));
  }, [debouncedSearch, filters, dataFlags, sort, page, perPage]);
  // Search is debounced 300ms; every OTHER filter change (facets/data/sort/page) refetches
  // immediately (load's identity changes → the effect below fires). No Apply button anywhere.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { load(); }, [load]);
  // Any filter/search/sort/per-page change resets to page 1 (skip mount so a URL-hydrated page
  // survives the initial render).
  const _firstFilterRun = useRef(true);
  useEffect(() => {
    if (_firstFilterRun.current) { _firstFilterRun.current = false; return; }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters, dataFlags, sort, perPage]);

  // --- toggle a facet option within a group (OR semantics) ---
  const toggleFacet = (group, id) => setFilters((f) => {
    const cur = f[group] || [];
    return { ...f, [group]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const toggleData = (id) => setDataFlags((d) => ({ ...d, [id]: !d[id] }));
  const resetFilters = () => { setFilters(DEFAULT_FILTERS); setDataFlags({ show_demo: false }); setSearch(""); };
  const onPerPage = (v) => { setPerPage(v); setPage(1); };

  // --- active-filter chips (built from selections + the option labels) ---
  const optLabel = (group, id) => (filterOptions[group] || []).find((o) => o.id === id)?.label || id;
  const chips = [];
  ACCT_FILTER_GROUPS.forEach((g) => (filters[g.id] || []).forEach((id) => {
    chips.push({ key: `${g.id}:${id}`, group: g.id, id, label: optLabel(g.id, id) });
  }));
  if (dataFlags.show_demo) chips.push({ key: "data:show_demo", group: "data", id: "show_demo", label: "Demo data" });
  const removeChip = (c) => { if (c.group === "data") toggleData(c.id); else toggleFacet(c.group, c.id); };
  const filterCount = chips.length;
  const loadMemSummary = useCallback(() => {
    api.get("/admin/analytics/memberships").then(({ data }) => setMemSummary(data)).catch(() => {});
  }, []);
  useEffect(() => { loadMemSummary(); }, [loadMemSummary]);

  // Deep-link from the Pending Users queue: /admin/accounts?open=<user_id> opens that drawer.
  const [urlParams, setUrlParams] = useSearchParams();
  useEffect(() => { const o = urlParams.get("open"); if (o) setOpenId(o); }, [urlParams]);
  // Keep the URL in sync with the current view (filters + search + sort + page + per-page) so it's
  // shareable / bookmarkable / Back-Forward-safe. Preserve any `open` deep-link param.
  useEffect(() => {
    const p = new URLSearchParams();
    if (debouncedSearch) p.set("search", debouncedSearch);
    ["status", "account_type", "role", "verification", "payout", "tier"].forEach((g) => {
      if (filters[g] && filters[g].length) p.set(g, filters[g].join(","));
    });
    if (dataFlags.show_demo) p.set("show_demo", "true");
    if (sort && sort !== "newest") p.set("sort", sort);
    if (perPage !== 25) p.set("per_page", String(perPage));
    if (page > 1) p.set("page", String(page));
    const open = urlParams.get("open");
    if (open) p.set("open", open);
    setUrlParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters, dataFlags, sort, perPage, page]);

  const cycleSortRole = () => { setSortTier(null); setSortRole((s) => (s === null ? "asc" : s === "asc" ? "desc" : null)); };
  const cycleSortTier = () => { setSortRole(null); setSortTier((s) => (s === null ? "desc" : s === "desc" ? "asc" : null)); };
  const filteredRows = rows;
  const displayRows = sortTier
    ? [...filteredRows].sort((a, b) => (tierRank(a.seller_tier) - tierRank(b.seller_tier)) * (sortTier === "asc" ? 1 : -1))
    : sortRole
    ? [...filteredRows].sort((a, b) => (roleRank(resolveAccountKind(a)) - roleRank(resolveAccountKind(b))) * (sortRole === "asc" ? 1 : -1))
    : filteredRows;

  // Admin rows are now bulk-selectable too — the destructive Purge action excludes them
  // server-side (they land in the "excluded" bucket), but they must be selectable so a
  // super-admin can include them in a bulk "Request ID verification". Super-admin only.
  const selectable = canBulk ? displayRows : [];
  const toggleOne = (uid) => setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const allSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.user_id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) selectable.forEach((u) => n.delete(u.user_id));
    else selectable.forEach((u) => n.add(u.user_id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkClassify = async (at) => {
    setClassifyMenu(false);
    try {
      const { data } = await api.post("/admin/accounts/bulk-account-type", { user_ids: [...selected], account_type: at });
      toast.success(`Classified ${data.changed} as ${at}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      clearSelection(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="text-foreground" data-testid="admin-accounts">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Users className="w-7 h-7 text-olive-bright" /> Users</h2>
      <p className="text-sm font-body text-secondary-foreground mt-2">Manage members, per-user category permissions, and login / IP activity.</p>
      <Crosshair className="my-5" />

      {hasSuperPrivileges(viewer?.role) && (
        <div className="mb-5 flex flex-wrap gap-2">
          <button onClick={() => setReverifyOpen(true)} data-testid="platform-reverify-btn"
            className="inline-flex items-center gap-1.5 border border-blaze/50 text-blaze hover:bg-blaze/10 px-3 py-2 font-mono uppercase text-xs transition-colors">
            <ShieldOff className="w-3.5 h-3.5" /> Platform re-verification
          </button>
          <button onClick={() => setPurge({ mode: "disabled", ids: [] })} data-testid="purge-disabled-btn"
            className="inline-flex items-center gap-1.5 border border-blaze/50 text-blaze hover:bg-blaze/10 px-3 py-2 font-mono uppercase text-xs transition-colors">
            <Trash2 className="w-3.5 h-3.5" /> Purge disabled accounts
          </button>
          <button onClick={() => setCleanupOpen(true)} data-testid="admin-cleanup-btn"
            className="inline-flex items-center gap-1.5 border border-[#d3a24e]/50 text-[#d3a24e] hover:bg-[#d3a24e]/10 px-3 py-2 font-mono uppercase text-xs transition-colors">
            <Gift className="w-3.5 h-3.5" /> Demote admins → Elite
          </button>
        </div>
      )}
      <PlatformReverifyModal open={reverifyOpen} onClose={() => setReverifyOpen(false)} onDone={load} />
      {cleanupOpen && <AdminCleanupDialog onClose={() => setCleanupOpen(false)} onDone={() => { setCleanupOpen(false); load(); }} />}

      <div className="flex items-center gap-2 mb-5 border-b border-sniper">
        {[["accounts", "Users"], ["ip-report", "IP / Login Report"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`accounts-tab-${id}`}
            className={`font-mono uppercase text-xs px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-olive-bright text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "accounts" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email / username / name"
                data-testid="accounts-search" className="w-full bg-input border border-sniper focus:border-olive outline-none pl-9 pr-3 py-2.5 text-foreground font-mono text-sm" />
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="accounts-sort"
              className="bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm uppercase">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <button onClick={() => setShowFilters((v) => !v)} data-testid="accounts-filter-toggle"
              className="lg:hidden inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2.5 transition-colors">
              Filters{filterCount > 0 ? ` (${filterCount})` : ""}
            </button>
          </div>

          {/* active-filter chips */}
          {filterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="accounts-active-chips">
              {chips.map((c) => (
                <button key={c.key} data-testid={`accounts-chip-${railSlug(c.key)}`} onClick={() => removeChip(c)}
                  className="inline-flex items-center gap-1.5 bg-olive/10 border border-olive/40 text-olive-bright font-mono text-[11px] uppercase px-2.5 py-1 hover:bg-olive/20 transition-colors">
                  {c.label} <X className="w-3 h-3" />
                </button>
              ))}
              <button onClick={resetFilters} data-testid="accounts-clear-all" className="font-mono text-[10px] uppercase text-blaze hover:underline">Clear all</button>
            </div>
          )}

          <div className={`grid grid-cols-1 gap-6 ${railCollapsed ? "lg:grid-cols-[40px_minmax(0,1fr)]" : "lg:grid-cols-[208px_minmax(0,1fr)]"}`}>
            {/* filter rail */}
            <aside className={`${showFilters ? "block" : "hidden"} lg:block`} data-testid="accounts-filter-rail">
              {railCollapsed ? (
                <div className="lg:sticky lg:top-4 hidden lg:flex flex-col items-center">
                  <button onClick={toggleRail} data-testid="accounts-rail-expand" title="Expand filters"
                    className="relative w-10 h-10 flex items-center justify-center border border-sniper bg-surface text-secondary-foreground hover:text-foreground hover:border-olive transition-colors">
                    <SlidersHorizontal className="w-4 h-4" />
                    {filterCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-olive text-white font-mono text-[9px] font-bold rounded-full flex items-center justify-center">{filterCount}</span>
                    )}
                  </button>
                </div>
              ) : (
                <div className="lg:sticky lg:top-4">
                  <div className="flex items-center justify-between h-9 mb-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={toggleRail} data-testid="accounts-rail-collapse" title="Collapse filters"
                        className="hidden lg:inline-flex text-muted-foreground hover:text-foreground transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <h3 className="font-heading text-lg uppercase tracking-tight leading-none">Filters</h3>
                    </div>
                    {filterCount > 0 && (
                      <button onClick={resetFilters} data-testid="accounts-rail-reset" className="text-[11px] font-mono uppercase text-blaze hover:underline flex items-center gap-1">
                        <X className="w-3 h-3" /> Clear all
                      </button>
                    )}
                  </div>
                  <div className="border border-sniper bg-surface p-4">
                    {ACCT_FILTER_GROUPS.map((g) => (
                      <RailGroup key={g.id} label={g.label}>
                        {(filterOptions[g.id] || []).map((opt) => (
                          <OptionRow
                            key={opt.id}
                            testid={`accounts-filter-${g.id}-${opt.id}`}
                            active={(filters[g.id] || []).includes(opt.id)}
                            label={opt.label}
                            count={facets?.[g.id]?.[opt.id]}
                            indent={opt.indent}
                            onClick={() => toggleFacet(g.id, opt.id)}
                          />
                        ))}
                      </RailGroup>
                    ))}
                    {/* DATA group — reveal demo / test accounts (boolean toggles) */}
                    <div className="border-t border-sniper/60 pt-4">
                      <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Data</h4>
                      <div className="space-y-1">
                        {(filterOptions.data || []).map((opt) => (
                          <OptionRow
                            key={opt.id}
                            testid={`accounts-filter-data-${opt.id}`}
                            active={!!dataFlags[opt.id]}
                            label={opt.label}
                            count={facets?.data?.[opt.id]}
                            onClick={() => toggleData(opt.id)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </aside>

            {/* results column */}
            <main className="min-w-0">
          {unclassified > 0 && !(filters.account_type || []).includes("unclassified") && (
            <div className="mb-4 font-mono text-[11px] text-amber-400" data-testid="accounts-unclassified-nudge">
              {unclassified} real account{unclassified === 1 ? "" : "s"} still unclassified (no account type set) ·{" "}
              <button onClick={() => toggleFacet("account_type", "unclassified")} data-testid="accounts-filter-unclassified" className="underline hover:text-volt">review &amp; classify</button>
            </div>
          )}
          {total === 0 ? (
            <div className="border border-sniper bg-surface p-10 text-center" data-testid="accounts-empty">
              <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="font-heading uppercase tracking-tight text-lg text-foreground">No accounts match</p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1">{filterCount > 0 || debouncedSearch ? "No accounts match the current filters and search." : "There are no accounts to show yet."}</p>
              {(filterCount > 0 || debouncedSearch) && (
                <button onClick={resetFilters} data-testid="accounts-empty-clear" className="mt-4 inline-flex items-center gap-1.5 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-[11px] px-4 py-2 transition-colors"><X className="w-3.5 h-3.5" /> Clear filters</button>
              )}
            </div>
          ) : (
          <>
          <Pager page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={onPerPage} testid="accounts-pager-top" />
          <div className={`transition-opacity ${fetching ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="hidden min-[900px]:block border border-sniper bg-surface overflow-x-auto" data-testid="accounts-table">
            {/* min-width keeps the flexible Account column >= ~240px on laptop widths (it collapsed to
                0 — showing only the USER role tag — once the Payout column was added); narrower
                viewports scroll horizontally instead. */}
            <table className="w-full min-w-[1000px] table-fixed font-mono text-xs">
              <colgroup>
                <col style={{ width: "40px" }} />
                <col />
                <col style={{ width: "72px" }} />
                <col style={{ width: "80px" }} />
                <col style={{ width: "112px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "128px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "88px" }} />
              </colgroup>
              <thead>
                <tr className="text-left text-muted-foreground uppercase text-[10px]">
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">
                    {canBulk && (
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="accounts-select-all"
                        className="accent-blaze cursor-pointer align-middle" aria-label="Select all" />
                    )}
                  </th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">Account</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2 text-right">Orders</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2 text-right">Listings</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">Joined</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">Status</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">Verify</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2">Payout</th>
                  <th className="sticky top-0 z-10 bg-surface border-b border-sniper px-3 py-2 text-right">Manage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sniper">
                {displayRows.length === 0 && <tr><td colSpan={9} className="px-3 py-3 text-muted-foreground">No accounts match.</td></tr>}
                {displayRows.map((u) => (
                  <tr key={u.user_id} className="text-secondary-foreground hover:bg-white/5 h-11" data-testid={`account-row-${u.username}`}>
                    <td className="px-3 py-2 align-middle">
                      {canBulk && (
                        <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleOne(u.user_id)}
                          data-testid={`account-checkbox-${u.username}`} className="accent-blaze cursor-pointer align-middle" aria-label={`Select ${u.username}`} />
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <div className="min-w-0">
                        <div className="text-foreground flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                          <span className="truncate" title={`@${u.username}`} data-testid={`account-handle-${u.username}`}>@{u.username}</span>
                          {u.banned && <Ban className="w-3 h-3 text-blaze shrink-0" />}
                          {u.email_verified && <BadgeCheck className="w-3 h-3 text-olive-bright shrink-0" />}
                          <RoleTag account={u} />
                          {(u.account_type || "").toLowerCase() === "ambassador" ? (
                            <span className="inline-flex items-center gap-1 text-[9px] uppercase font-mono px-1.5 py-0.5 border" style={{ color: "#E08A4F", borderColor: "#C2571F80" }} data-testid={`account-ambassador-${u.username}`}>
                              <Megaphone className="w-2.5 h-2.5" /> Amb · {u.ambassador_fee?.fee_pct != null ? `${u.ambassador_fee.fee_pct}%` : "7.5%"}
                            </span>
                          ) : (
                            <TierStack u={u} />
                          )}
                          {u.is_demo && <span className="text-[9px] uppercase text-amber-400 border border-amber-400/50 px-1" data-testid={`demo-tag-${u.username}`}>demo</span>}
                          {u.is_test && <span className="text-[9px] uppercase text-muted-foreground border border-muted-foreground/40 px-1" data-testid={`test-tag-${u.username}`}>test</span>}
                        </div>
                        <div className="text-[11px] truncate" data-testid={`account-name-${u.username}`} title={u.real_name || ""}>{u.real_name ? <span className="text-secondary-foreground">{u.real_name}{u.show_name_public && <span className="ml-1 text-[8px] uppercase text-olive-bright/70 border border-olive-bright/30 px-1 not-italic" title="Member shows their name publicly">public</span>}</span> : <span className="italic text-[#6B6D70]">{u.identity_status === "verified" ? "— no name on file" : "name pending ID verification"}</span>}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right align-middle tabular-nums" data-testid={`account-orders-${u.username}`}>{u.orders_count ?? 0}</td>
                    <td className="px-3 py-2 text-right align-middle tabular-nums" data-testid={`account-listings-${u.username}`}>{u.listing_count}</td>
                    <td className="px-3 py-2 align-middle text-muted-foreground whitespace-nowrap" data-testid={`account-joined-${u.username}`}>{fmtJoined(u.created_at)}</td>
                    <td className="px-3 py-2 align-middle" data-testid={`account-status-${u.username}`}>
                      <StatusChip state={(u.access_status || "active").toLowerCase()} />
                    </td>
                    <td className="px-3 py-2 align-middle" data-testid={`account-verify-${u.username}`}>
                      <StatusChip {...idChipState(u)} />
                    </td>
                    <td className="px-3 py-2 align-middle" data-testid={`account-payout-${u.username}`}>
                      <PayoutChip payout={u.payout} username={u.username} />
                    </td>
                    <td className="px-3 py-2 text-right align-middle">
                      <button onClick={() => setOpenId(u.user_id)} data-testid={`account-manage-${u.username}`}
                        className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-foreground font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* MOBILE cards — below ~900px, the squeezed table becomes a card list (Item 12) */}
          <div className="min-[900px]:hidden space-y-2" data-testid="accounts-cards">
            {displayRows.map((u) => (
              <div key={u.user_id} className="border border-sniper bg-surface p-3 flex items-start justify-between gap-3" data-testid={`account-card-${u.username}`}>
                <div className="min-w-0 flex items-start gap-2">
                  {canBulk && (
                    <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleOne(u.user_id)}
                      data-testid={`account-card-checkbox-${u.username}`} className="accent-blaze cursor-pointer mt-1 shrink-0" aria-label={`Select ${u.username}`} />
                  )}
                  <div className="min-w-0">
                    {/* Handle first */}
                    <div className="text-foreground flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                      <span className="truncate font-mono text-sm">@{u.username}</span>
                      {u.banned && <Ban className="w-3 h-3 text-blaze shrink-0" />}
                      {u.email_verified && <BadgeCheck className="w-3 h-3 text-olive-bright shrink-0" />}
                      {u.is_demo && <span className="text-[9px] uppercase text-amber-400 border border-amber-400/50 px-1">demo</span>}
                      {u.is_test && <span className="text-[9px] uppercase text-muted-foreground border border-muted-foreground/40 px-1">test</span>}
                    </div>
                    {/* Name below */}
                    <div className="text-[11px] mt-0.5" data-testid={`account-card-name-${u.username}`}>{u.real_name ? <span className="text-secondary-foreground">{u.real_name}</span> : <span className="italic text-[#6B6D70]">{u.identity_status === "verified" ? "— no name on file" : "name pending ID verification"}</span>}</div>
                    {/* Role / membership on one line */}
                    <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                      <RoleTag account={u} />
                      {(u.account_type || "").toLowerCase() === "ambassador" ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase font-mono px-1.5 py-0.5 border" style={{ color: "#E08A4F", borderColor: "#C2571F80" }}>
                          <Megaphone className="w-2.5 h-2.5" /> Amb · {u.ambassador_fee?.fee_pct != null ? `${u.ambassador_fee.fee_pct}%` : "7.5%"}
                        </span>
                      ) : (
                        <TierStack u={u} />
                      )}
                    </div>
                    {/* Status chips + counts */}
                    <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                      <StatusChip state={(u.access_status || "active").toLowerCase()} />
                      <StatusChip {...idChipState(u)} />
                      <PayoutChip payout={u.payout} username={`card-${u.username}`} />
                      <span className="font-mono text-[10px] text-muted-foreground tabular-nums">· {u.orders_count ?? 0} orders · {u.listing_count} listings</span>
                    </div>
                  </div>
                </div>
                {/* MANAGE right-aligned */}
                <button onClick={() => setOpenId(u.user_id)} data-testid={`account-card-manage-${u.username}`}
                  className="shrink-0 inline-flex items-center gap-1 border border-sniper hover:border-olive text-foreground font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                  Manage
                </button>
              </div>
            ))}
          </div>
          </div>
          <Pager page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={onPerPage} testid="accounts-pager-bottom" />
          </>
          )}
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <div className="text-[11px] font-mono uppercase text-muted-foreground space-y-1">
              <p>
                <span className="text-olive-bright" data-testid="accounts-admin-count">{adminCount} admin{adminCount === 1 ? "" : "s"} platform-wide</span>
              </p>
              {memSummary && (
                <p className="normal-case flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="accounts-membership-summary">
                  <span className="uppercase text-muted-foreground">Members:</span>
                  <span className="text-olive-bright uppercase" data-testid="summary-paying">{memSummary.paid_members} paying</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-volt uppercase inline-flex items-center gap-1" data-testid="summary-comped"><Gift className="w-3 h-3" />{memSummary.comped_members} comped <span className="text-muted-foreground normal-case">(not revenue)</span></span>
                  {memSummary.by_tier && Object.keys(memSummary.by_tier).length > 0 && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      {["elite", "preferred", "standard"].filter((t) => memSummary.by_tier[t]).map((t) => (
                        <span key={t} className={`uppercase ${(TIER_META[t] || TIER_META.standard).cls.split(" ")[0]}`}>{memSummary.by_tier[t]} {t}</span>
                      ))}
                    </>
                  )}
                  <span className="text-muted-foreground">·</span>
                  <span className="text-secondary-foreground uppercase" data-testid="summary-mrr">MRR {fmtMoney(memSummary.mrr_cents)}</span>
                </p>
              )}
              {hasSuperPrivileges(viewer?.role) && (
                <div className="pt-1 flex flex-wrap items-center gap-2">
                  <BetaCompPanel onChanged={loadMemSummary} />
                  <BetaTesterBulkGrant onChanged={loadMemSummary} />
                </div>
              )}
              <FoundingCompsExpiring />
              <BetaOverridesNeedingReview onOpen={setOpenId} />
              <ListingFeeOverridesReview />
              {hasSuperPrivileges(viewer?.role) && <ResendSyncPanel />}
              {admins.length > 0 && (
                <p data-testid="accounts-admin-list" className="normal-case text-secondary-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="uppercase text-muted-foreground">Admins (click to manage):</span>
                  {admins.map((a) => (
                    <button key={a.user_id} onClick={() => setOpenId(a.user_id)}
                      data-testid={`admin-list-${a.username}`}
                      title={a.email}
                      className="text-foreground hover:text-olive-bright underline decoration-dotted underline-offset-2 transition-colors">
                      @{a.username}{a.role === "super_admin" ? " (super)" : ""}
                    </button>
                  ))}
                </p>
              )}
            </div>
          </div>
            </main>
          </div>

          {/* Bulk action bar — SAFE actions are primary; purge is tucked into an
              overflow danger-zone and is hidden entirely once the site is live. */}
          {canBulk && selected.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-black/95 border border-sniper px-4 py-3 shadow-lg" data-testid="bulk-bar">
              <span className="font-mono text-xs uppercase text-foreground">{selected.size} selected</span>
              <button onClick={() => setVerifyReq({ ids: [...selected] })} data-testid="bulk-request-verify-btn"
                className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                <MailCheck className="w-3 h-3" /> Request ID verification
              </button>
              <button onClick={() => setResendReq({ rows: rows.filter((u) => selected.has(u.user_id)) })} data-testid="bulk-resend-email-btn"
                className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                <MailCheck className="w-3 h-3" /> Resend email verification
              </button>
              <div className="relative">
                <button onClick={() => setClassifyMenu((v) => !v)} data-testid="bulk-classify-btn"
                  className="inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-3 py-1.5 transition-colors">
                  <Tag className="w-3 h-3" /> Set account type
                </button>
                {classifyMenu && (
                  <div className="absolute bottom-full mb-1 left-0 bg-surface border border-sniper min-w-[150px]" data-testid="bulk-classify-menu">
                    <button onClick={() => bulkClassify("individual")} data-testid="bulk-classify-individual" className="w-full text-left px-3 py-2 font-mono uppercase text-[10px] text-secondary-foreground hover:bg-white/5 hover:text-foreground">Individual</button>
                    <button onClick={() => bulkClassify("business")} data-testid="bulk-classify-business" className="w-full text-left px-3 py-2 font-mono uppercase text-[10px] text-secondary-foreground hover:bg-white/5 hover:text-foreground">Business</button>
                  </div>
                )}
              </div>
              <button onClick={clearSelection} data-testid="bulk-clear-btn" className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground">Clear selection</button>
              {bulkPurgeEnabled && (
                <div className="relative border-l border-sniper pl-3">
                  <button onClick={() => setBulkMore((v) => !v)} data-testid="bulk-more-btn"
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] px-1.5 py-1.5">
                    <MoreHorizontal className="w-4 h-4" /> More
                  </button>
                  {bulkMore && (
                    <div className="absolute bottom-full mb-1 right-0 bg-surface border border-blaze/40 min-w-[210px]" data-testid="bulk-more-menu">
                      <div className="px-3 py-1.5 font-mono uppercase text-[9px] text-blaze/70 border-b border-blaze/20">Danger zone · irreversible</div>
                      <button onClick={() => { setBulkMore(false); setPurge({ mode: "selected", ids: [...selected] }); }} data-testid="purge-selected-btn"
                        className="w-full text-left inline-flex items-center gap-1.5 px-3 py-2 text-blaze hover:bg-blaze/10 font-mono uppercase text-[10px] transition-colors">
                        <Trash2 className="w-3 h-3" /> Purge selected…
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {verifyReq && (
            <RequestVerifyModal ids={verifyReq.ids} onClose={() => setVerifyReq(null)}
              onDone={() => { setVerifyReq(null); clearSelection(); load(); }} />
          )}
          {purge && (
            <PurgeDialog mode={purge.mode} ids={purge.ids} onClose={() => setPurge(null)}
              onDone={() => { setPurge(null); clearSelection(); load(); }} />
          )}
          {resendReq && (
            <BulkResendEmailModal rows={resendReq.rows} onClose={() => setResendReq(null)}
              onDone={() => { setResendReq(null); clearSelection(); load(); }} />
          )}
        </>
      ) : (
        <IpReport />
      )}

      {openId && <AccountDrawer userId={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
}
